/**
 * El catálogo canónico.
 *
 * `es` es la fuente: aquí se escribe la copy y de aquí sale el tipo que `en.ts`
 * tiene que cumplir. La organización es por dominio y espeja `src/features/`,
 * para que buscar dónde vive un texto sea el mismo gesto que buscar dónde vive
 * la pantalla.
 *
 * La terminología no se inventa: `docs/first_release/02-glosario.md` fija el
 * vocabulario del producto en las dos lenguas (empleado, ficha, capacidad,
 * política, escalado, acuerdo…) y es la referencia cuando haya duda. Lo que
 * NO se traduce, por contrato de `00-README §4.1`: identificadores, nombres de
 * entidad, endpoints, `slug`s y los `code` de error.
 */
export const es = {
  jenkins: {
    approvalHint: 'Esta aprobación autoriza una sola operación con los siguientes parámetros.',
    companyScope: 'Job no asociado a ningún proyecto: se ejecuta solo con los valores por defecto de Jenkins.',
    parameters: 'Parámetros',
    "connectionIntro": "Conecta el Jenkins de tu empresa para asignar un pipeline a cada proyecto y consultar o ejecutar sus builds.",
    "connectionState": "Estado de la conexión",
    "url": "URL de Jenkins",
    "username": "Usuario de Jenkins",
    "token": "Token API",
    "tokenHint": "Se valida y guarda cifrado. El token no se vuelve a mostrar ni se entrega a los empleados IA.",
    "rotate": "Validar y sustituir token",
    "connect": "Conectar Jenkins",
    "verify": "Comprobar conexión",
    "verified": "Conexión verificada. Jenkins reconoce el usuario y permite consultar su API.",
    "verifyFailed": "No se pudo verificar la conexión.",
    "disconnect": "Desconectar Jenkins",
    "disconnectHint": "Los proyectos conservarán su historial, pero dejarán de acceder a Jenkins hasta que vuelvas a conectarlo y revises sus asociaciones.",
    "permissionsHint": "La cuenta de Jenkins necesita Overall/Read y Job/Read, Job/Build y Job/Cancel sobre los pipelines autorizados. Utiliza un token API de una cuenta de integración.",
    "noPipeline": "Sin pipeline asociado",
    "selectHint": "Selecciona un pipeline del Jenkins de la empresa. En multibranch, entra hasta la rama que quieras ejecutar.",
    "openJenkins": "Abrir en Jenkins",
    "companyConnection": "Configurar conexión de empresa",
    "configure": "Configurar pipeline",
    "selectPipeline": "Seleccionar pipeline",
    "noJobs": "No hay pipelines o carpetas visibles aquí.",
    "selected": "Pipeline seleccionado",
    "autonomyLabel": "Autonomía de los empleados IA",
    "autonomyHint": "Además del modo elegido, cada empleado necesita acceso al proyecto y las capacidades jenkins.read, jenkins.build o jenkins.cancel. El gerente las concede en Permisos.",
    "autonomy": {
        "read_only": "Solo consulta",
        "approval": "Requiere aprobación humana",
        "autonomous": "Operación autónoma"
    },
    "parameterRules": "Parámetros que puede cambiar la IA",
    "rulesHint": "Marca los valores permitidos o escribe uno por línea. Si no concedes alternativas, el empleado utilizará el valor predeterminado de Jenkins.",
    "unbind": "Quitar asociación",
    "unsupported": "Este pipeline tiene parámetros no compatibles; ejecútalo desde Jenkins",
    "disabled": "Pipeline deshabilitado",
    "launch": "Lanzar pipeline",
    "relaunch": "Volver a lanzar",
    "launchHint": "Se creará una ejecución nueva con estos parámetros. Comprueba la rama y la versión antes de lanzarla. Para builds externos se muestran los valores actuales de Jenkins.",
    "cancelBuild": "Cancelar ejecución",
    "confirmCancel": "Se solicitará cancelar esta ejecución o elemento de la cola de Jenkins:",
    "queue": "En cola",
    "builds": "Ejecuciones de Jenkins",
    "noBuilds": "Este pipeline todavía no tiene ejecuciones.",
    "operations": "Operaciones de la aplicación",
    "noOperations": "Todavía no se han solicitado operaciones desde la aplicación.",
    "openApprovals": "Abrir aprobaciones",
    "console": "Consola",
    "consoleHint": "Salida de Jenkins con secretos ocultos. Se conserva en pantalla hasta un millón de caracteres recientes.",
    "consoleLimited": "Se ha alcanzado el límite de lectura segura. Consulta el resto de la salida directamente en Jenkins.",
    "emptyConsole": "Todavía no hay salida disponible.",
    "reconcileHint": "Comprueba en Jenkins qué build corresponde a esta solicitud y vincula su número para recuperar el seguimiento. Esta acción no vuelve a lanzar el pipeline.",
    "buildNumber": "Número de build comprobado",
    "reconcile": "Vincular build existente",
    "status": {
        "pending_approval": "Pendiente de aprobación",
        "ready": "Pendiente de envío",
        "submitting": "Enviando a Jenkins",
        "queued": "En cola",
        "running": "En ejecución",
        "succeeded": "Completada",
        "failed": "Fallida",
        "cancelled": "Cancelada",
        "rejected": "Rechazada",
        "expired": "Aprobación caducada",
        "invalidated": "Requiere una nueva solicitud",
        "unknown": "Pendiente de conciliación"
    }
},
  kubernetes: {
    title: 'Kubernetes',
    connectionIntro: 'Pega el kubeconfig de tu empresa para ver el clúster, conectar cada proyecto a sus namespaces y dejar que los empleados IA lo consulten u operen según sus capacidades.',
    connectionState: 'Estado de la conexión',
    kubeconfig: 'Kubeconfig',
    kubeconfigHint: 'Solo se admiten datos en línea: token o certificado de cliente y CA en base64. Se rechazan plugins exec, ficheros externos e insecure-skip-tls-verify. El documento se valida contra su contexto actual y se guarda cifrado; no se vuelve a mostrar ni se entrega a los empleados IA.',
    uploadFile: 'Cargar un fichero',
    connect: 'Conectar clúster',
    rotate: 'Validar y sustituir kubeconfig',
    verify: 'Comprobar conexión',
    verified: 'Conexión verificada: el contexto {context} responde con la versión {version}.',
    verifyFailed: 'No se pudo verificar la conexión.',
    disconnect: 'Desconectar clúster',
    disconnectHint: 'Los proyectos conservarán su historial, pero dejarán de acceder al clúster hasta que vuelvas a conectarlo y revises sus vinculaciones.',
    permissionsHint: 'La cuenta del kubeconfig necesita get, list y watch sobre lo que quieras ver; patch sobre deployments, statefulsets, daemonsets y cronjobs para operar; delete sobre pods; create sobre jobs y pods/exec; y patch o delete sobre los tipos permitidos para aplicar manifiestos. El API server debe ser una dirección HTTPS pública.',
    contexts: 'Contextos del kubeconfig',
    noContexts: 'El kubeconfig no define contextos.',
    currentContext: 'Contexto actual',
    context: 'Contexto',
    cluster: 'Clúster',
    server: 'API server',
    user: 'Usuario',
    defaultNamespace: 'Namespace por defecto',
    auth: { token: 'Token', client_cert: 'Certificado de cliente' },
    certExpires: 'El certificado caduca el {date}',
    version: 'Versión',
    platform: 'Plataforma',
    notConnected: 'Sin clúster conectado',
    notConnectedHint: 'Conecta el kubeconfig de la empresa en Ajustes → Kubernetes para ver el clúster.',
    openSettings: 'Abrir ajustes de Kubernetes',
    clusterTitle: 'Clúster',
    clusterIntro: 'Vista de solo lectura del clúster de la empresa. Las acciones se hacen desde la pestaña Kubernetes de cada proyecto.',
    nodes: 'Nodos',
    noNodes: 'La cuenta del kubeconfig no puede listar nodos, o el clúster no tiene ninguno visible.',
    console: {
      namespace: 'Namespace',
      noNamespaces: 'La cuenta no puede listar namespaces. Escribe uno.',
      kind: 'Tipo de recurso',
      empty: 'No hay recursos de este tipo en el namespace.',
      truncated: 'La lista está acotada a los primeros quinientos recursos.',
      describe: 'Describir',
      logs: 'Logs',
      events: 'Eventos',
      noEvents: 'Sin eventos recientes en este namespace.',
      copy: 'Copiar',
      copied: 'Copiado',
      redacted: 'Se han ocultado valores que parecían secretos.',
      format: { yaml: 'YAML', json: 'JSON' },
      open: 'Ver {name}',
      close: 'Cerrar detalle',
      refresh: 'Actualizar',
    },
    kinds: {
      workloads: 'Cargas de trabajo',
      deployments: 'Deployments',
      statefulsets: 'StatefulSets',
      daemonsets: 'DaemonSets',
      jobs: 'Jobs',
      cronjobs: 'CronJobs',
      pods: 'Pods',
      services: 'Services',
      ingresses: 'Ingresses',
      configmaps: 'ConfigMaps',
      secrets: 'Secrets',
      events: 'Eventos',
      nodes: 'Nodos',
    },
    columns: {
      name: 'Nombre',
      kind: 'Tipo',
      status: 'Estado',
      roles: 'Roles',
      version: 'Versión',
      allocatable: 'Asignable',
      conditions: 'Condiciones',
      ready: 'Listos',
      upToDate: 'Actualizados',
      available: 'Disponibles',
      age: 'Edad',
      node: 'Nodo',
      restarts: 'Reinicios',
      phase: 'Fase',
      type: 'Tipo',
      clusterIp: 'IP interna',
      ports: 'Puertos',
      class: 'Clase',
      hosts: 'Hosts',
      addresses: 'Direcciones',
      keys: 'Claves',
      schedule: 'Programación',
      suspended: 'Suspendido',
      lastSchedule: 'Última ejecución',
      completions: 'Completados',
      images: 'Imágenes',
      reason: 'Motivo',
      object: 'Objeto',
      message: 'Mensaje',
      count: 'Veces',
      lastSeen: 'Última vez',
      container: 'Contenedor',
    },
    phase: { Pending: 'Pendiente', Running: 'En ejecución', Succeeded: 'Terminado', Failed: 'Fallido', Unknown: 'Desconocido', Terminating: 'Terminando' },
    workloadStatus: { Available: 'Disponible', Progressing: 'Progresando', 'Scaled to zero': 'A cero', Running: 'En ejecución', Complete: 'Completado', Failed: 'Fallido', Active: 'Activo', Suspended: 'Suspendido' },
    nodeStatus: { Ready: 'Listo', NotReady: 'No listo' },
    unschedulable: 'Acordonado',
    yes: 'Sí',
    no: 'No',
    secretsHint: 'De los secrets solo se muestran nombre y tipo. Sus valores nunca salen del clúster hacia esta aplicación.',
    configmapsHint: 'De los configmaps se listan las claves; el contenido se consulta al describir, con los valores sospechosos ocultos.',
    logs: {
      container: 'Contenedor',
      previous: 'Contenedor anterior (tras un reinicio)',
      timestamps: 'Marcas de tiempo',
      hint: 'Salida del contenedor con secretos ocultos. Se conserva en pantalla hasta un millón de caracteres recientes.',
      limited: 'Se ha alcanzado el límite de lectura segura: hay más de lo que cabe en una página para el mismo segundo. Consulta ese tramo con kubectl.',
      empty: 'Todavía no hay salida disponible.',
    },
    age: { seconds: '{count}s', minutes: '{count}m', hours: '{count}h', days: '{count}d' },
    binding: {
      title: 'Vinculación del proyecto',
      noBinding: 'Sin clúster vinculado',
      selectHint: 'Elige un contexto del kubeconfig de la empresa y los namespaces sobre los que trabaja este proyecto. Las acciones y los empleados IA se limitan a ellos.',
      context: 'Contexto',
      namespaces: 'Namespaces',
      namespacesHint: 'Marca los namespaces del proyecto. La cuenta del kubeconfig debe poder listar pods en cada uno; se comprueba al guardar.',
      extraNamespaces: 'Otros namespaces (uno por línea)',
      autonomyLabel: 'Autonomía de los empleados IA',
      autonomyHint: 'Además del modo elegido, cada empleado necesita acceso al proyecto y las capacidades kubernetes.read, kubernetes.operate o kubernetes.apply. Aplicar y borrar manifiestos siempre pide aprobación humana, incluso en modo autónomo.',
      autonomy: { read_only: 'Solo consulta', approval: 'Requiere aprobación humana', autonomous: 'Operación autónoma' },
      configure: 'Configurar vinculación',
      unbind: 'Quitar vinculación',
      companyConnection: 'Configurar el kubeconfig de la empresa',
      summary: '{context} · {namespaces}',
    },
    actions: {
      scale: 'Escalar',
      replicas: 'Réplicas',
      scaleHint: 'Se cambiará el número de réplicas y se esperará a que el rollout termine.',
      restart: 'Reiniciar rollout',
      confirmRestart: 'Se reiniciará el rollout de {name}: los pods se sustituirán uno a uno.',
      deletePod: 'Borrar pod',
      confirmDeletePod: 'Se borrará el pod {name}. Su controlador creará otro si corresponde.',
      suspend: 'Suspender',
      resume: 'Reanudar',
      runNow: 'Ejecutar ahora',
      confirmSuspend: 'Se suspenderá el CronJob {name}: no se programarán nuevas ejecuciones.',
      confirmResume: 'Se reanudará el CronJob {name}.',
      confirmRun: 'Se creará un Job a partir del CronJob {name} ahora mismo.',
      delete: 'Borrar',
      confirmDelete: 'Se borrará {kind}/{name} del namespace {namespace}. Escribe el nombre para confirmar.',
      request: 'Solicitar',
      requested: 'Operación solicitada',
    },
    apply: {
      title: 'Aplicar manifiesto',
      hint: 'Pega uno o varios documentos YAML. El servidor los valida en modo dry-run y muestra qué cambiaría antes de aplicar nada.',
      namespace: 'Namespace por defecto',
      manifest: 'Manifiesto YAML',
      dryRun: 'Previsualizar cambios',
      preview: 'Cambios previstos',
      create: 'Se creará',
      update: 'Se actualizará',
      noChanges: 'Sin cambios',
      force: 'Forzar: tomar el control de campos que gestiona otra herramienta',
      submit: 'Aplicar',
      edit: 'Editar manifiesto',
      failed: 'El servidor rechazó el manifiesto',
    },
    operations: {
      title: 'Operaciones de la aplicación',
      empty: 'Todavía no se han solicitado operaciones desde la aplicación.',
      openApprovals: 'Abrir aprobaciones',
      reconcile: 'Conciliar',
      reconcileHint: 'La respuesta del clúster se perdió. Comprueba en el clúster si el cambio se aplicó y cierra la operación como completada o fallida; no se reintentará sola.',
      succeeded: 'Se aplicó',
      failed: 'No se aplicó',
      invalidated: 'La vinculación cambió desde esta solicitud.',
      manifests: '{count} manifiestos',
    },
    action: { scale: 'Escalar', rollout_restart: 'Reiniciar rollout', delete_pod: 'Borrar pod', suspend_cronjob: 'Suspender CronJob', resume_cronjob: 'Reanudar CronJob', run_cronjob: 'Ejecutar CronJob', apply: 'Aplicar manifiesto', delete: 'Borrar recurso' },
    status: {
      pending_approval: 'Pendiente de aprobación',
      ready: 'Pendiente de envío',
      submitting: 'Enviando al clúster',
      observing: 'Observando el resultado',
      succeeded: 'Completada',
      failed: 'Fallida',
      rejected: 'Rechazada',
      expired: 'Aprobación caducada',
      invalidated: 'Requiere una nueva solicitud',
      unknown: 'Pendiente de conciliación',
    },
    exec: {
      title: 'Terminal',
      open: 'Abrir terminal',
      container: 'Contenedor',
      command: 'Comando',
      connecting: 'Conectando…',
      connected: 'Conectado',
      closed: 'Sesión cerrada',
      exited: 'El proceso terminó con código {code}',
      error: 'No se pudo abrir la terminal.',
      close: 'Cerrar terminal',
      hint: 'La sesión queda auditada (quién, pod, contenedor, comando, bytes y duración), nunca su contenido. Dura como máximo treinta minutos y se cierra tras diez de inactividad.',
      smallScreen: 'La terminal necesita una pantalla más ancha.',
    },
    approval: { hint: 'Esta aprobación autoriza una sola operación sobre el clúster, exactamente la que se describe.', target: 'Objetivo', manifest: 'Manifiesto' },
  },
  feedback: {
    acceptPermission: 'Aceptar requiere permiso para crear tareas.',
    connect: 'Conectar esta app', intro: 'El backend de tu app puede enviar texto y audio. La IA preparará una propuesta para que la revises y la conviertas en tarea.',
    projectId: 'Identificador del proyecto', guide: 'Guía pública de integración', filter: 'Estado', empty: 'Esta app todavía no tiene feedback en este estado.', select: 'Selecciona un envío para revisarlo.',
    original: 'Mensaje original', transcript: 'Transcripción', audio: 'Audio original', downloadAudio: 'Descargar audio', audioDeleted: 'El audio se eliminó expresamente.',
    deleteAudio: 'Eliminar audio', deleteAudioHint: 'El audio se borrará de forma permanente. La transcripción y la propuesta se conservarán.',
    processingError: 'No se ha podido completar el procesamiento. El envío se conserva. Motivo:', proposal: 'Propuesta de cambio',
    discard: 'Descartar', accept: 'Aceptar y encolar', saved: 'Propuesta guardada', queued: 'Tarea creada y puesta en cola', openTask: 'Abrir tarea #{number}',
    status: { received: 'Recibido', processing: 'Procesando', pending_review: 'Pendiente de revisión', accepted: 'Aceptado', discarded: 'Descartado', failed: 'Error de procesamiento' },
    fields: { title: 'Título', summary: 'Resumen', description: 'Descripción', acceptance_criteria: 'Criterios de aceptación', type: 'Tipo', priority: 'Prioridad' },
    types: { feature: 'Funcionalidad', bug: 'Error', chore: 'Mantenimiento', docs: 'Documentación', test: 'Pruebas', research: 'Investigación', security: 'Seguridad' },
    priorities: { low: 'Baja', normal: 'Normal', high: 'Alta', urgent: 'Urgente' },
    company: {
      title: 'Feedback pendiente ({count})',
      intro: 'Envíos de todas las apps de esta empresa que aún esperan una decisión: primero los listos para revisar, después los fallidos y por último los que la IA sigue procesando.',
      groups: { pending_review: 'Listos para revisar', failed: 'Fallidos: decide si reintentar o descartar', in_flight: 'En proceso' },
      emptyTitle: 'Todo en orden',
      quipLoading: 'Preguntando a la IA…',
      quipFallback: 'Todo en orden. O eso parece.',
      aiBadge: 'IA',
      aiHint: 'Frase generada por la IA conectada a esta empresa.',
    },
    key: { title: 'Integración de feedback', hint: 'Una clave para los proyectos de esta empresa. Guárdala en el backend de cada app. Permite enviar feedback y consultar su estado.', active: 'Clave activa · termina en {hint}', generate: 'Generar clave', hide: 'Ocultar', rotate: 'Rotar clave', rotateHint: 'La clave anterior dejará de funcionar inmediatamente. Después, copia la nueva y actualiza los backends de tus apps.' },
  },
  common: {
    loading: 'Cargando',
    smallScreen: 'Mejor en una pantalla grande.',
    smallScreenHint: 'Aquí abajo está todo, pero esta pantalla se lee y se maneja mucho mejor desde un ordenador.',
    save: 'Guardar',
    saveChanges: 'Guardar cambios',
    cancel: 'Cancelar',
    close: 'Cerrar',
    create: 'Crear',
    edit: 'Editar',
    remove: 'Quitar',
    delete: 'Eliminar',
    archive: 'Archivar',
    retry: 'Reintentar',
    tryAgain: 'Reintentar',
    back: 'Atrás',
    next: 'Siguiente',
    previous: 'Anterior',
    finish: 'Terminar',
    search: 'Buscar',
    filter: 'Filtrar',
    all: 'Todas',
    none: 'Ninguno',
    yes: 'Sí',
    no: 'No',
    optional: 'opcional',
    required: 'obligatorio',
    copy: 'Copiar',
    copied: 'Copiado',
    open: 'Abrir',
    view: 'Ver',
    refresh: 'Actualizar',
    more: 'Más',
    less: 'Menos',
    showMore: 'Ver más',
    showLess: 'Ver menos',
    never: 'Nunca',
    unknown: 'Desconocido',
    saving: 'Guardando…',
    of: 'de',
    step: 'Paso {current} de {total}',
    list: {
      item: '{label} {index}',
      moveUp: 'Subir {label} {index}',
      moveDown: 'Bajar {label} {index}',
      remove: 'Quitar {label} {index}',
      add: 'Añadir a «{label}»',
    },
  },

  errors: {
    generic: 'Algo ha ido mal.',
    network: 'No se ha podido contactar con el servidor. Revisa la conexión y vuelve a intentarlo.',
    forbidden: 'No tienes permiso para hacer eso.',
    notFound: 'No se ha encontrado.',
    copyReference: 'Copia esta referencia para soporte',
  },

  confirm: {
    typeToConfirm: 'Escribe {name} para confirmar',
    nameDoesNotMatch: 'El nombre no coincide',
  },

  theme: {
    label: 'Tema',
    light: 'Claro',
    dark: 'Oscuro',
    system: 'Sistema',
    toggleToDark: 'Activar el tema oscuro',
    toggleToLight: 'Activar el tema claro',
  },

  nav: {
    brand: 'AbsorStaff',
    skipToContent: 'Saltar al contenido',
    companies: 'Empresas',
    approvals: 'Aprobaciones',
    notifications: 'Notificaciones',
    account: 'Cuenta',
    signOut: 'Cerrar sesión',
    closeMenu: 'Cerrar el menú',
    openSidebar: 'Abrir la navegación',
    systemStatus: 'Sistema operativo',
    newCompany: 'Nueva empresa',
    workspace: 'Espacio de trabajo',
    switchCompany: 'Cambiar de empresa',
    global: 'Espacio de trabajo',
  },

  company: {
    notAvailable: 'Esa empresa no está disponible',
    notAvailableHint:
      'Puede que se haya archivado, o que el enlace sea incorrecto. Si acabas de crearla, recarga la página.',
    backToCompanies: 'Volver a tus empresas',
    sections: {
      overview: 'Resumen',
      organization: 'Organización',
      work: 'Trabajo',
      settings: 'Ajustes',
    },
    tabs: {
      jenkins: 'Jenkins',
      kubernetes: 'Kubernetes',
      cluster: 'Clúster',
      panel: 'Panel',
      org: 'Organigrama',
      employees: 'Empleados',
      departments: 'Departamentos',
      teams: 'Equipos',
      permissions: 'Permisos',
      projects: 'Proyectos',
      tasks: 'Tareas',
      feedback: 'Feedback',
      approvals: 'Aprobaciones',
      runs: 'Ejecuciones',
      conversations: 'Conversaciones',
      agreements: 'Acuerdos',
      costs: 'Costes',
      autonomy: 'Autonomía',
      general: 'General',
      credentials: 'Credenciales',
      budgets: 'Presupuestos',
      github: 'GitHub',
      repos: 'Repositorios',
      webhooks: 'Webhooks',
      audit: 'Auditoría',
      danger: 'Zona de peligro',
    },
  },

  auth: {
    signIn: 'Iniciar sesión',
    email: 'Correo electrónico',
    password: 'Contraseña',
    newPassword: 'Contraseña nueva',
    mfaCode: 'Código de verificación',
    mfaHint: 'El código de seis dígitos de tu aplicación de autenticación, o un código de recuperación.',
    verify: 'Verificar',
    createAccount: 'Crear una cuenta',
    forgotPassword: '¿Has olvidado la contraseña?',
    backToSignIn: 'Volver a iniciar sesión',

    documentation: {
      title: 'Documentación para personas e IA',
      hint: 'Guía de integración y ejemplos para conectar tu app.',
      markdown: 'Markdown para agentes',
      openapi: 'Referencia OpenAPI',
    },

    reset: {
      title: 'Restablecer la contraseña',
      subtitle: 'Te enviaremos un enlace por correo.',
      send: 'Enviar el enlace',
      sentTitle: 'Revisa tu correo',
      sentSubtitle: 'Si esa dirección tiene una cuenta, el enlace de restablecimiento ya va de camino.',
      chooseTitle: 'Elige una contraseña nueva',
      passwordHint: 'Al menos 12 caracteres. Habrá que volver a iniciar sesión en todos los demás sitios.',
      change: 'Cambiar la contraseña',
      doneTitle: 'Contraseña cambiada',
      doneSubtitle: 'Se ha cerrado cualquier otra sesión, incluidas las que no hayas abierto tú.',
    },

    register: {
      title: 'Crea tu cuenta',
      name: 'Nombre',
      passwordHint: 'Al menos 12 caracteres.',
      submit: 'Crear la cuenta',
      sentTitle: 'Revisa tu correo',
      sentSubtitle: 'Si esa dirección se puede registrar, el enlace de verificación ya va de camino.',
      sentHint: 'El enlace caduca pronto. Volver a pedir el registro envía uno nuevo.',
      haveAccount: '¿Ya tienes cuenta?',
    },

    verifyEmail: {
      workingTitle: 'Verificando tu correo',
      failedTitle: 'Ese enlace no ha funcionado',
      failedSubtitle: 'Los enlaces de verificación son de un solo uso y caducan.',
      failedOpenHint:
        'Registrarse otra vez con la misma dirección envía un enlace nuevo: es la misma petición, así que no se crea nada dos veces.',
      requestNewLink: 'Pedir un enlace nuevo',
      failedClosedHint: 'Pídele un enlace nuevo a quien creó tu cuenta.',
      missingToken: 'A ese enlace le falta el testigo.',
      doneTitle: 'Correo verificado',
      doneSubtitle: 'Ya puedes iniciar sesión.',
    },
  },

  panel: {
    yourCompanies: 'Tus empresas',
    newCompany: 'Nueva empresa',
    noCompanies: 'Aún no hay empresas',
    noCompaniesHint: 'Una empresa es donde viven los empleados, los proyectos y el presupuesto. Empieza por una.',
    createFirst: 'Crea tu primera empresa',
    intro: 'Cómo está la empresa ahora mismo: gasto del mes, actividad y lo que le falta para funcionar sola.',
    thisMonth: 'Este mes',
    readiness: 'Puesta a punto',
    fixThis: 'Reparar',
    fixThisLabel: 'Proponer arreglos para {check}',
    openRepairs: 'Proponer arreglos',
    addCredential: 'Añadir una credencial',
    counts: {
      employees: 'Empleados',
      projects: 'Proyectos',
      activeTasks: 'Tareas activas',
      awaitingApproval: 'Esperando aprobación',
    },
  },

  status: {
    company: {
      active: 'Activa',
      paused: 'En pausa',
      archived: 'Archivada',
    },
    check: {
      ok: 'Correcto',
      warning: 'Aviso',
      error: 'Error',
      not_applicable: 'No aplica',
    },
  },

  settings: {
    account: {
      title: 'Tu cuenta',
      name: 'Nombre',
      email: 'Correo electrónico',
      verified: 'Verificado.',
      notVerified: 'Sin verificar: no podrás crear una empresa hasta que lo esté.',
      timezone: 'Zona horaria',
      mfaTitle: 'Verificación en dos pasos',
      mfaEnabled: 'Activada. Se te pide un código al iniciar sesión.',
      mfaDisabled: 'Sin activar. Activarla protege tu cuenta incluso si tu contraseña se filtra.',
      appearance: 'Apariencia',
      saved: 'Ajustes guardados',
    },

    notifications: {
      title: 'Preferencias de notificación',
      intro:
        'Sin reglas, todo llega a tu bandeja dentro de la aplicación y lo de severidad {warning} o superior se envía además por correo. Las reglas afinan eso: gana la más específica —una regla (empresa, tipo) gana a una (empresa, *), que a su vez gana a una para todas las empresas—.',
      company: 'Empresa',
      allCompanies: 'Todas las empresas',
      type: 'Tipo de notificación',
      inApp: 'En la aplicación',
      email: 'Correo',
      minSeverity: 'Severidad mínima',
      removeRule: 'Quitar la regla',
      addRule: 'Añadir una regla',
      savePreferences: 'Guardar las preferencias',
      saved: 'Preferencias guardadas',
    },
  },

  severity: {
    info: 'Información',
    warning: 'Aviso',
    error: 'Error',
    critical: 'Crítica',
  },

  notifications: {
    bell: 'Notificaciones',
    bellUnread: 'Notificaciones, {count} sin leer',
    title: 'Notificaciones',
    unread: '{count} sin leer',
    markAllRead: 'Marcar todas como leídas',
    state: 'Estado',
    state_unread: 'Sin leer',
    state_read: 'Leídas',
    state_all: 'Todas',
    severity: 'Severidad',
    company: 'Empresa',
    nothingUnread: 'No hay nada sin leer',
    nothingUnreadHint: 'Lo que vayas leyendo sale de aquí. Las leídas están en el selector de estado.',
    nothingYet: 'Aquí no hay nada todavía',
    emptyHint:
      'Las aprobaciones pendientes, las tareas fallidas, los umbrales de presupuesto y las propuestas de acuerdo aterrizan aquí en cuanto ocurren.',
    unreadMark: 'sin leer',
  },

  approvals: {
    title: 'Aprobaciones pendientes ({count})',
    intro:
      'Rechazar siempre pide un motivo: ese texto llega al agente, que lo reconsidera en vez de detenerse.',
    companyIntro:
      'Las compuertas abiertas en esta empresa. Rechazar siempre pide un motivo: ese texto llega al agente, que lo reconsidera en vez de detenerse.',
    companyNothingHint:
      'Cuando una compuerta de esta empresa pida una persona —una fusión en ruta protegida, una contratación, un plan— aparecerá aquí y en tu bandeja del espacio de trabajo.',
    approveSelected: 'Aprobar {count} seleccionadas',
    nothingTitle: 'No necesitan nada de ti',
    nothingHint:
      'Las aprobaciones aparecen aquí cuando una compuerta pide una persona: una fusión en una ruta protegida, una contratación, un plan que necesita visto bueno.',
    unknownCompany: 'Empresa desconocida',
    blocksTask: 'bloquea su tarea',
    expires: 'Caduca el {date}: después la tarea se bloquea y te espera igualmente.',
    rejectPlaceholder:
      '¿Por qué no? El agente lee esto y lo reconsidera; es la parte de la que aprende.',
    rejectWithReason: 'Rechazar con este motivo',
    reject: 'Rechazar',
    approve: 'Aprobar',
    selectForBulk: 'Seleccionar para aprobación en lote',
  },

  tasks: {
    title: 'Tareas',
    intro:
      'Un tablero por proyecto. Las tarjetas abren la tarea, donde viven la línea temporal, la traza en vivo y las acciones; el buscador cruza todos los proyectos.',
    tabBlocked: {
      one: '{count} bloqueada',
      other: '{count} bloqueadas',
    },
    boards: 'Tableros por proyecto',
    noProject: 'Sin proyecto',
    search: 'Buscar en todos los proyectos',
    searchHint: 'Por título o por número, en todos los proyectos y también en el archivo.',
    clearSearch: 'Borrar la búsqueda',
    searchResults: {
      zero: 'Nada coincide con «{query}» en ningún proyecto.',
      one: '{count} resultado para «{query}» en todos los proyectos',
      other: '{count} resultados para «{query}» en todos los proyectos',
    },
    searchMore: 'Hay más resultados de los que se enseñan: afina la búsqueda.',
    noBoards: 'Aún no hay tareas',
    noBoardsHint: 'Cada proyecto tiene su tablero, y aparece aquí en cuanto existe. Crea un proyecto, o una tarea suelta.',
    openBoard: 'Ver el tablero',
    newTask: 'Nueva tarea',
    emptyColumn: 'Vacía',
    ofTotal: '{shown} de {total}',
    blockedBanner: {
      one: '{count} tarea bloqueada espera a una persona:',
      other: '{count} tareas bloqueadas esperan a una persona:',
    },
    unassigned: 'Sin asignar',
    risk: 'riesgo {level}',
    showArchived: 'Ver archivadas',
    backToBoard: 'Volver al tablero',
    archive: 'Archivar la tarea #{number}',
    unarchive: 'Desarchivar la tarea #{number}',
    archivedBadge: 'Archivada',
    archivedEmpty:
      'El archivo está vacío. En «Terminadas», el botón de la esquina de cada tarjeta guarda aquí las que ya no necesitas ver; nada se borra.',
    archiveFinished: {
      one: 'Archivar la tarea terminada',
      other: 'Archivar las {count} tareas terminadas',
    },
    archiveFinishedTitle: 'Archivar las terminadas',
    archiveFinishedBody: {
      one: 'La tarea terminada del tablero pasa a «Ver archivadas». No cambia de estado y no se pierde nada.',
      other:
        'Las {count} tareas terminadas del tablero —hechas, falladas y canceladas, también las que la columna no llega a enseñar— pasan a «Ver archivadas». No cambian de estado y no se pierde nada, pero volver a sacarlas es tarjeta a tarjeta.',
    },
    archiveFinishedProject: 'Solo las del tablero «{project}».',
    columns: {
      queue: 'Cola',
      working: 'En marcha',
      review: 'Revisión',
      pr: 'PR, CI y despliegue',
      approval: 'Aprobación',
      blocked: 'Bloqueadas',
      finished: 'Terminadas',
    },
    status: {
      draft: 'Borrador',
      queued: 'En cola',
      triaged: 'Triada',
      assigned: 'Asignada',
      meeting: 'En reunión',
      planning: 'Planificando',
      in_progress: 'En curso',
      in_review: 'En revisión',
      qa: 'QA',
      pr_open: 'PR abierta',
      awaiting_ci: 'Esperando CI',
      awaiting_deploy: 'Esperando despliegue',
      awaiting_approval: 'Esperando aprobación',
      blocked: 'Bloqueada',
      done: 'Hecha',
      failed: 'Fallida',
      cancelled: 'Cancelada',
    },
    priority: {
      low: 'Baja',
      normal: 'Normal',
      high: 'Alta',
      urgent: 'Urgente',
    },
    type: {
      feature: 'Funcionalidad',
      bug: 'Error',
      chore: 'Mantenimiento',
      research: 'Investigación',
      refactor: 'Refactor',
      docs: 'Documentación',
      test: 'Pruebas',
      ops: 'Operaciones',
      security: 'Seguridad',
      new_project: 'Proyecto nuevo',
      review: 'Revisión',
    },
    riskLevel: {
      low: 'bajo',
      medium: 'medio',
      high: 'alto',
    },
  },

  runs: {
    title: 'Ejecuciones',
    intro:
      'Todo lo que corre ahora mismo, con su traza según ocurre. Pausar conserva el espacio de trabajo para poder retomar la sesión donde se quedó; cancelar destruye el contenedor.',
    nothingRunning: 'No hay nada en marcha',
    nothingRunningHint:
      'Las ejecuciones aparecen aquí en cuanto un empleado se pone a trabajar. Las terminadas conservan su traza y se leen desde la tarea a la que pertenecen.',
    steps: '{count} pasos',
    step: {
      tool: 'Herramienta',
      denied: 'Denegado: {tool}',
      thinking: 'Razonando',
      error: 'Error',
      system: 'Sistema',
      interrupted: 'Interrumpida',
      assistant: 'Asistente',
    },
  },

  taskDetail: {
    blockedCode: 'Código',
    suggested: {
      budget_exhausted: 'Ampliar el presupuesto',
      no_assignee: 'Arreglar la plantilla del proyecto',
      capability_missing: 'Arreglar la plantilla del proyecto',
      dependency: 'Ver las dependencias',
      no_progress: 'Leer la última ejecución',
      escalated: 'Leer la última ejecución',
      not_reproducible: 'Añadir pasos de reproducción',
      model_refusal: 'Reformular la tarea en un comentario',
      approval_expired: 'Ir a aprobaciones',
      queue_full: 'Ver los límites de la empresa',
      agent_stopped: 'Contestar al agente en un comentario',
      conversation_loop: 'Leer la última ejecución',
      jenkins_deploy_failed: 'Relanzar en la pestaña Jenkins y desbloquear',
      jenkins_deploy_rejected: 'Ir a Jenkins y desbloquear para pedirlo otra vez',
      jenkins_deploy_timeout: 'Comprobar el pipeline en Jenkins',
      kubernetes_invalidated: 'Revisar la vinculación de Kubernetes y pedirlo otra vez',
      jenkins_invalidated: 'Revisar la asociación de Jenkins y pedirlo otra vez',
    },
    forcedHint:
      '{name} hace {phase} siendo {held}: nadie en el proyecto tiene el rol {wanted}, así que la revisión la firma quien quedaba.',
    forcedFix: 'Arreglar la plantilla del proyecto',
    notFound: 'No existe la tarea #{number}',
    notFoundHint: 'Puede que pertenezca a otra empresa, o que el número no sea el correcto.',
    sections: 'Secciones de la tarea',
    tabs: {
      timeline: 'Línea temporal',
      runs: 'Ejecuciones',
      cost: 'Coste',
      dependencies: 'Dependencias',
      phases: 'Fases',
      conversations: 'Conversaciones',
    },
    blocked: 'Bloqueada',
    failed: 'Fallida',
    waitingOn: 'Esperando a {tasks}: mira la pestaña de dependencias.',
    unassigned: 'sin asignar',
    review: 'revisión: {name}',
    pendingApprovals: {
      one: '{count} aprobación pendiente',
      other: '{count} aprobaciones pendientes',
    },
    actions: {
      queue: 'Poner en cola',
      assignAuto: 'Asignar automáticamente',
      assignManual: 'Asignar a…',
      assignPick: 'Elige a un empleado',
      confirmAssign: 'Asignar',
      assignUnpublished:
        'La ficha de {name} no está publicada: la tarea se bloqueará al intentar abrir su sesión. Publícala desde su ficha de empleado.',
      retry: 'Reintentar',
      retryStuck: 'Desatascar',
      unblock: 'Desbloquear',
      cancel: 'Cancelar la tarea',
      cancelReason: '¿Por qué? El motivo queda registrado.',
      cancelClosePr: 'Cerrar también la PR #{number} en GitHub',
      cancelClosePrHint:
        'Se cierra sin fusionar y con el motivo como comentario. La rama se conserva y la PR puede reabrirse desde GitHub. Apagado, la PR se queda abierta bajo una tarea cancelada.',
      confirmCancel: 'Confirmar la cancelación',
      keepIt: 'Dejarla',
      commentPlaceholder: 'Comentario: el agente lo lee en su siguiente turno.',
      comment: 'Comentar',
      reopen: 'Reabrir con ajustes',
      reopenReason: '¿Qué falta o qué hay que ajustar?',
      reopenHint:
        'El agente parte del código ya mergeado, hace solo estos cambios en una rama nueva y abre otro PR; la tarea vuelve a pasar por revisión, QA y merge.',
      confirmReopen: 'Reabrir la tarea',
      archive: 'Archivar',
      unarchive: 'Desarchivar',
      teamMeeting: 'Reunión previa con el equipo',
      teamMeetingHint:
        'Antes de planificar, quien lidere el proyecto pregunta a todo el equipo y escribe un acta que se lee aquí mismo y que recibe la planificación. Cuesta una sesión por asistente y retrasa el arranque unos minutos.',
      teamMeetingLocked: 'La tarea ya arrancó: su recorrido está decidido y la reunión ya no cabe delante.',
      awaitDeploy: 'Hasta el despliegue en Jenkins',
      awaitDeployHint:
        'Tras el merge, la tarea espera a que el pipeline de Jenkins del proyecto despliegue con éxito antes de darse por hecha. Solo aplica si el proyecto tiene Jenkins conectado; si no, termina al mergear.',
      awaitDeployLocked: 'La PR ya se mergeó: el despliegue está en marcha y el interruptor ya no cambia nada.',
    },
  },

  taskPanels: {
    noRuns: 'Aún no hay ejecuciones. Aparecen en cuanto una fase empieza a ejecutarse.',
    noPerTaskLimit: 'sin límite por tarea',
    ofLimit: 'de {limit} ({percent})',
    runCount: '{count} ejecuciones',
    byPhase: 'Por fase',
    byModel: 'Por modelo',
    phase: 'Fase',
    model: 'Modelo',
    runs: 'Ejecuciones',
    tokensIn: 'Tokens de entrada',
    tokensOut: 'Tokens de salida',
    cost: 'Coste',
    waitsFor: 'Espera a',
    waitedForBy: 'La esperan',
    subtasks: 'Subtareas',
    blocking: 'bloqueante',
    addDependency: 'Añadir una dependencia',
    chooseTask: 'Elige una tarea…',
    dependencyType: 'Tipo de dependencia',
    blocksThisTask: 'bloquea esta tarea',
    relatesToIt: 'se relaciona con ella',
    link: 'Enlazar',
  },

  taskConversations: {
    label: 'Conversaciones de la tarea',
    intro:
      'Lo que los empleados se dijeron a cuenta de esta tarea y de sus subtareas, con un resumen de lo que se hizo en cada hilo. El resumen se deriva del hilo y de las ejecuciones que hablaron; no lo escribe ningún modelo.',
    empty: 'Nadie ha hablado con nadie por esta tarea',
    emptyHint: 'Cuando un empleado pregunte, delegue o escale a cuenta de la tarea, el hilo aparecerá aquí.',
    subtask: 'subtarea #{number}',
    pair: '{from} → {to}',
    pairCount: {
      one: '{count} mensaje',
      other: '{count} mensajes',
    },
    pairBlocked: {
      one: '{count} bloqueado',
      other: '{count} bloqueados',
    },
    kind: {
      ask: 'pregunta',
      delegation: 'delegación',
      escalation: 'escalado',
      notification: 'aviso',
      chat: 'mensaje',
    },
    outcome: {
      answered: 'contestada',
      awaiting: 'esperando respuesta',
      unanswered: 'sin respuesta',
      delegated: 'delegada',
      delivered: 'entregado',
      blocked: 'bloqueado',
    },
    sentence: {
      answered: '{from} preguntó a {to}; {by} contestó en {duration}.',
      answeredLate:
        '{from} preguntó a {to}; {by} contestó en {duration}, cuando el plazo de la pregunta ya había vencido: {from} siguió sin la respuesta.',
      awaiting: '{from} preguntó a {to} y todavía espera la respuesta.',
      unanswered: '{from} preguntó a {to} y nadie contestó dentro del plazo.',
      escalated: '{from} escaló a {to}; {by} contestó en {duration}.',
      escalatedAwaiting: '{from} escaló a {to} y todavía espera la respuesta.',
      escalatedUnanswered: '{from} escaló a {to} y nadie contestó dentro del plazo.',
      delegated: '{from} delegó en {to} la tarea #{number} «{title}», que está {status}.',
      delegatedUnknown: '{from} delegó una tarea en {to}.',
      notified: '{from} avisó a {to}.',
      chat: '{from} escribió a {to}.',
      blocked: '{from} intentó escribir a {to} y la política lo paró.',
      someone: 'alguien',
      nobody: 'nadie',
    },
    work: 'Qué se hizo',
    workEntry: '{name} · {phase} · {task}',
    stillRunning: 'Todavía en marcha: contará lo que hizo cuando termine.',
    noSummary: 'La ejecución terminó sin contar lo que hizo ({reason}).',
    noWork: 'Ninguna ejecución ha contado todavía lo que hizo en este hilo.',
    openTask: 'Abrir la tarea #{number}',
    showMessages: {
      one: 'Ver el mensaje',
      other: 'Ver los {count} mensajes',
    },
    hideMessages: 'Ocultar los mensajes',
    blockedAttempts: {
      one: '{count} intento bloqueado',
      other: '{count} intentos bloqueados',
    },
    interCompany: 'entre empresas',
  },

  journey: {
    label: 'Fases de la tarea',
    empty: 'Esta tarea no tiene plan que dibujar.',
    hint: 'Pulsa una fase para ver quién la hizo, cuánto duró y sus ejecuciones.',
    smallScreenHint: 'El diagrama de fases se lee mejor en una pantalla grande.',
    nodeLabel: '{name}: {state}',
    round: 'vuelta {round}',
    times: '×{count}',
    attempts: {
      one: '{count} intento',
      other: '{count} intentos',
    },
    phase: {
      triage: 'Triaje',
      meeting: 'Reunión de equipo',
      planning: 'Planificación',
      implementation: 'Implementación',
      self_review: 'Autorrevisión',
      peer_review: 'Revisión',
      qa: 'QA',
      pr: 'Pull request',
      fix: 'Arreglo',
      ops: 'Operaciones',
    },
    performer: {
      assignee: 'asignado',
      facilitator: 'facilitador',
      reviewer: 'revisor',
      second_reviewer: 'segundo revisor',
      qa: 'QA',
      system: 'sistema',
    },
    node: {
      intake: 'Entrada',
      ci: 'CI',
      merge: 'Merge',
      deploy: 'Despliegue',
      approval: 'Aprobación',
      blocked: 'Bloqueo',
      end: 'Fin',
    },
    state: {
      pending: 'pendiente',
      skipped: 'omitida por la política',
      active: 'en curso',
      done: 'hecha',
      failed: 'fallida',
      blocked: 'bloqueada',
      cancelled: 'cancelada',
    },
    selected: {
      actor: 'Quién',
      started: 'Empezó',
      ended: 'Terminó',
      duration: 'Duración',
      cost: 'Coste',
      outcome: 'Motivo',
      viewRuns: 'Ver ejecuciones',
      noRuns: 'Sin ejecuciones en esta fase.',
    },
  },

  timeline: {
    nothingYet: 'Todavía no ha pasado nada.',
    events: {
      created: 'Creada',
      queued: 'En cola',
      triaged: 'Triada',
      assigned: 'Asignada',
      plan_ready: 'Plan listo',
      run_started: 'Ejecución iniciada',
      run_finished: 'Ejecución terminada',
      review_requested: 'Revisión solicitada',
      changes_requested: 'Cambios solicitados',
      review_passed: 'Revisión superada',
      qa_passed: 'QA superado',
      qa_failed: 'QA fallido',
      pr_opened: 'PR abierta',
      ci_passed: 'CI en verde',
      ci_failed: 'CI en rojo',
      approval_requested: 'Aprobación solicitada',
      approved: 'Aprobada',
      rejected: 'Rechazada',
      merged: 'Fusionada',
      blocked: 'Bloqueada',
      unblocked: 'Desbloqueada',
      failed: 'Fallida',
      cancelled: 'Cancelada',
      retried: 'Reintentada',
      reopened: 'Reabierta',
      escalated: 'Escalada',
      comment: 'Comentario',
      skipped: 'Omitida',
      meeting_held: 'Reunión celebrada',
      phase_continued: 'Fase continúa',
      deploy_requested: 'Despliegue solicitado',
      deployed: 'Desplegado',
    },
    actor: {
      user: 'persona',
      employee: 'empleado',
      system: 'sistema',
      github: 'GitHub',
    },
    minutes: {
      show: 'Ver el acta',
      hide: 'Ocultar el acta',
    },
  },

  newTask: {
    title: 'Nueva tarea',
    intro:
      'La descripción es lo que lee el empleado asignado. Cuanto mejor especificada esté, más barato sale el resultado.',
    type: 'Tipo',
    priority: 'Prioridad',
    taskTitle: 'Título',
    description: 'Descripción',
    descriptionHint: 'Qué hacer, dónde y qué importa. El agente lo lee literalmente.',
    acceptance: 'Criterios de aceptación',
    acceptanceHint: 'Opcional. Cómo decide la fase de QA que el trabajo está hecho.',
    project: 'Proyecto',
    assignee: 'Responsable',
    letRouterDecide: 'Que decida el enrutador',
    assigneeRequiredWithoutProject: 'Sin proyecto no hay candidatos: elige quién atiende la tarea para poder encolarla.',
    askEmployee: 'Preguntar a un empleado',
    askEmployeeHint: 'Crea una tarea de operaciones para que un empleado compruebe, responda o actúe sobre el clúster o Jenkins.',
    repositories: 'Repositorios',
    budgetLimit: 'Límite de presupuesto (USD)',
    budgetHint: '0 significa sin límite por tarea; el presupuesto de la empresa se sigue aplicando.',
    labels: 'Etiquetas',
    labelsHint: 'Separadas por comas.',
    teamMeeting: 'Reunión previa con el equipo',
    teamMeetingHint:
      'Antes de planificar, quien lidere el proyecto pregunta a todo el equipo y escribe un acta que la planificación recibe y que se lee en la tarea. Cuesta una sesión por asistente: enciéndela cuando el cómo esté genuinamente por decidir.',
    awaitDeploy: 'Hasta el despliegue en Jenkins',
    awaitDeployHint:
      'Tras el merge, la tarea espera a que el pipeline de Jenkins del proyecto despliegue con éxito antes de darse por hecha. Solo aplica si el proyecto tiene Jenkins conectado; si no, termina al mergear.',
    queueNow: 'Ponerla en cola ya',
    queueHint:
      'Apagado, la tarea se crea como borrador que puedes editar y encolar después. Encendido, el orquestador la coge de inmediato.',
    createAndQueue: 'Crear y encolar',
    createDraft: 'Crear el borrador',
  },

  runViewer: {
    showingLast: 'mostrando los últimos {shown} de {total}',
    windowed: 'los últimos {count} en pantalla',
    mode: 'Cómo mirar la ejecución',
    modeSimple: 'Sencillo',
    modeTerminal: 'Terminal',
    fullscreen: 'Pantalla completa',
    exitFullscreen: 'Salir de pantalla completa',
    backToBottom: 'Volver al final',
    working: 'trabajando…',
    starting: 'Arrancando la ejecución…',
    noSteps: 'Esta ejecución no dejó ningún paso.',
    errorsInRun: '{count} con error en esta ejecución',
    whatItReported: 'Lo que ha reportado',
    requested: '{status} · {action} solicitado',
    attempt: 'intento {number}',
    turns: '{count} turnos',
    tools: '{count} herramientas',
    pause: 'Pausar',
    pausing: 'Pausando…',
    cancel: 'Cancelar',
    cancelling: 'Cancelando…',
    resume: 'Reanudar',
    live: 'En vivo',
    degraded: 'Las actualizaciones en vivo no están disponibles; la traza de abajo sigue completa hasta ahora.',
    connecting: 'Conectando…',
    raceLost: 'La ejecución terminó antes de poder aplicar la acción: acabó como {status}.',
    queued: 'Esperando hueco',
    queuedHint: 'En cola. Arranca en cuanto se libere un hueco; todavía no se ha ejecutado nada.',
    whyItStopped: 'Por qué se paró',
    outcome: {
      interrupted:
        'La plataforma le quitó la sesión: el agente no ha fallado. Se continúa sola y no gasta ninguno de los intentos de la fase.',
      agentFault: 'La sesión se ejecutó y no entregó. La fase tiene otro intento si le queda alguno.',
      withheld:
        'Se paró antes de poder trabajar. Reintentar no cambia nada hasta que cambie un presupuesto o una decisión.',
    },
  },

  assist: {
    title: 'Asistente',
    empty: 'Cuenta qué hace este puesto. La ficha se va rellenando aquí al lado.',
    thinking: 'Escribiendo la ficha…',
    validating: 'Comprobando lo escrito…',
    messageLabel: 'Mensaje para el asistente',
    placeholder: 'Que revise también los PR del equipo de facturación.',
    send: 'Enviar',
  },

  orgAssist: {
    title: 'Construir la organización',
    intro:
      'Cuenta cómo quieres que funcione la empresa y el asistente arma el plan: departamentos, equipos y a quién contratar. Nada se crea hasta que lo apliques.',
    open: 'Construir con el asistente',
    describe: 'Cómo es tu empresa',
    describePlaceholder:
      'Somos una consultora de software de ocho personas. Hay un CTO, dos equipos de producto y alguien que lleva la calidad de todo lo que sale.',
    describeHint: 'Con unas frases basta. Lo que falte se pregunta, y lo que ya existe el asistente lo tiene delante.',
    start: 'Empezar',
    waiting: 'Esperando la primera propuesta del asistente.',
    departments: 'Departamentos',
    teams: 'Equipos',
    templates: 'Plantillas nuevas',
    templatesHint:
      'Solo las que hacen falta porque ninguna del catálogo encaja. Quedan guardadas y se puede volver a contratar desde ellas.',
    newTemplate: 'nueva',
    roles: 'A contratar',
    name: 'Nombre',
    slug: 'Slug',
    mission: 'Misión',
    purpose: 'Propósito',
    inside: 'Dentro de',
    topLevel: 'Primer nivel',
    department: 'Departamento',
    team: 'Equipo',
    label: 'Nombre del rol',
    jobTitle: 'Puesto',
    seniority: 'Seniority',
    description: 'Descripción',
    template: 'Plantilla',
    reportsTo: 'Reporta a',
    nobodyTop: 'Nadie: cabeza de la empresa',
    removeLabel: 'Quitar {name} del plan',
    apply: 'Aplicar el plan',
    confirmTitle: 'Aplicar el plan',
    confirmIntro: 'Se crea todo de una vez. Si algo fallara no se crearía nada.',
    estimatedTokens: 'Unos {count} tokens de prompt de sistema entre todos, en cada tarea que ejecuten.',
  },

  staffingAssist: {
    title: 'Asignar personal con el asistente',
    intro:
      'Cuenta qué hace falta en este proyecto y el asistente propone quién trabaja en él, con qué rol, qué dedicación y qué acceso al código. Nada cambia hasta que lo apliques.',
    open: 'Asignar con el asistente',
    openHint: 'Propone altas, cambios, bajas y, si nadie encaja, contratar.',
    describe: 'Qué necesita este proyecto',
    describePlaceholder:
      'Vamos a meter la pasarela de pagos este trimestre. Bruno puede llevarlo, pero hace falta alguien que revise y no tenemos a nadie de QA.',
    describeHint:
      'El asistente ya sabe quién está asignado hoy, qué repositorios tiene el proyecto y a quién puedes contratar.',
    start: 'Empezar',
    plan: 'Plan de personal',
    groupRemove: 'Bajas',
    groupHire: 'Contrataciones',
    groupAdd: 'Altas',
    groupUpdate: 'Cambios',
    groupKeep: 'Sin cambios',
    groupKeepHint: 'Se quedan exactamente como están.',
    opRemove: 'Baja',
    opHire: 'Contratar',
    opAdd: 'Alta',
    opUpdate: 'Cambio',
    opKeep: 'Sigue igual',
    who: 'Quién',
    hireName: 'Nombre',
    template: 'Plantilla',
    reportsTo: 'Reporta a',
    role: 'Rol en el proyecto',
    allocation: 'Dedicación %',
    access: 'Acceso a repositorios',
    accessNone: 'Ninguno',
    accessRead: 'Lectura',
    accessWrite: 'Escritura',
    accessAdmin: 'Administración',
    repositories: 'Repositorios',
    allRepositories: 'Todos los del proyecto',
    someRepositories: 'Solo los marcados',
    reason: 'Motivo',
    demote: 'Dar de baja',
    demoteLabel: 'Dar de baja a {name}',
    undoRemove: 'Deshacer la baja',
    undoRemoveLabel: 'Deshacer la baja de {name}',
    dropLabel: 'Quitar {name} del plan',
    removedLabel: 'Baja: {name}',
    apply: 'Aplicar el plan',
    nothingToApply: 'El plan todavía no cambia nada.',
    confirmTitle: 'Aplicar el plan de personal',
    confirmIntro: 'Se aplica todo de una vez. Si algo fallara no cambiaría nada.',
    countHire: 'Contrataciones',
    countAdd: 'Altas',
    countUpdate: 'Cambios',
    countRemove: 'Bajas',
    leaving: 'Salen del proyecto: {names}',
    unpublished: 'Las fichas de quien se contrate nacen sin publicar: hay que publicarlas antes de que puedan trabajar.',
    estimatedTokens: 'Unos {count} tokens de prompt de sistema por cada contratación, en cada tarea que ejecuten.',
  },

  readinessAssist: {
      check: {
        chainOfCommand: 'Cadena de mando',
        profiles: 'Fichas publicadas',
        capabilities: 'Capacidades coherentes',
        lead: 'Responsable del proyecto',
        repository: 'Repositorio del proyecto',
        pipeline: 'Cobertura del pipeline',
        oversight: 'Revisión y QA con su propio rol',
        idleMembers: 'Miembros que no reciben trabajo',
        llm: 'Credencial de IA',
        github: 'Credencial de GitHub',
        access: 'Acceso a los repositorios',
        equipped: 'Empleados con capacidades',
        consistency: 'Políticas coherentes',
      },
    title: 'Arreglar la configuración',
    intro:
      'Los dos verificadores dicen qué impide que esta empresa funcione: uno si está puesta a punto y otro si una tarea llegaría de «encolada» a «hecha». Aquí el asistente propone los cambios que lo arreglan, se revisan y se aplican de una vez.',
    open: 'Proponer arreglos',
    seedMessage:
      'Propón qué cambiar para que los verificadores dejen de quejarse. Empieza por lo que no cuesta nada: capacidades que faltan, selectores de política que no casan con nadie y gente que ya está en plantilla. Las credenciales no las propongas: esas las pega una persona.',
    seedFocus: 'Céntrate en «{check}».',
    groupLoose: 'Sin queja asociada',
    groupLooseHint: 'Filas que no dicen a qué comprobación contestan. Puede ser correcto, pero conviene revisarlas.',
    op: {
      grant_capability: 'Conceder',
      revoke_capability: 'Revocar',
      set_member: 'Asignar al proyecto',
      set_project_lead: 'Nombrar responsable',
      set_manager: 'Poner bajo un responsable',
      hire: 'Contratar',
      archive_repository: 'Sacar de servicio',
      sweep_reservation: 'Liberar el nombre',
      fix_policy_selector: 'Corregir el selector',
      set_policy_priority: 'Cambiar la prioridad',
      disable_policy: 'Apagar la política',
      create_team: 'Crear el equipo',
    },
    employee: 'Quién',
    capability: 'Capacidad',
    capabilities: 'Capacidades al contratar',
    project: 'Proyecto',
    manager: 'Reporta a',
    hireName: 'Nombre',
    policy: 'Política',
    policyOff: 'apagada',
    subjectSelector: 'Quién habla',
    targetSelector: 'Con quién',
    priority: 'Prioridad',
    repositoryName: 'Repositorio',
    teamName: 'Nombre del equipo',
    teamSlug: 'Slug del equipo',
    accessUnchanged: 'Dejar como está',
    dropLabel: 'Quitar {name} del plan',
    apply: 'Aplicar los arreglos',
    confirmTitle: 'Aplicar los arreglos',
    confirmIntro:
      'Se escribe todo de una vez y el verificador vuelve a pasar. Si algo empeorara, no se aplicaría nada.',
    confirmHires:
      'Las fichas de quien se contrate nacen sin publicar, y una ficha sin publicar no cuenta para ninguna comprobación: la queja seguirá abierta hasta que la publiques.',
  },

  templates: {
    title: 'Plantillas de empleado',
    intro: 'Los roles desde los que esta empresa contrata: los que se ha escrito y los doce que trae la plataforma.',
    manage: 'Plantillas',
    own: 'Las de esta empresa',
    platform: 'Las de la plataforma',
    platformHint: 'Vienen con el producto y están siempre disponibles.',
    noneYet: 'Esta empresa aún no se ha escrito ninguna',
    noneYetHint: 'Cuando ninguno de los doce roles encaje, el asistente escribe uno contigo y queda guardado aquí.',
    deleteLabel: 'Eliminar {name}',
    deleteConfirm:
      'Deja de poder contratarse desde ella. Quien ya se contratara así no se ve afectado: su ficha se copió al contratar, no apunta aquí.',
  },

  templateCreator: {
    title: 'Crear una plantilla',
    intro: 'Describe el puesto y la IA de la empresa rellena los cuatro bloques. Se itera hasta que convence, y al aceptar queda guardada.',
    describe: 'Qué puesto quieres',
    describePlaceholder: 'Un ingeniero de datos para el almacén: monta y mantiene las tuberías de ingesta, y responde de que los números cuadren.',
    describeHint: 'Con dos o tres frases basta. Lo que falte se pregunta o se deduce.',
    start: 'Empezar',
    waiting: 'Esperando la primera propuesta del asistente.',
    label: 'Nombre de la plantilla',
    labelHint: 'Lo pone el asistente; es lo que se lee en el selector de contratación.',
    accept: 'Aceptar y guardar',
    fromTemplate: 'Crear una plantilla con IA',
  },

  employees: {
    title: 'Empleados',
    intro: 'Cada empleado tiene una ficha que se compila en el prompt de sistema bajo el que corre.',
    startBlank: 'Empezar en blanco',
    hireFromTemplate: 'Contratar desde una plantilla',
    hireFirst: 'Contrata a tu primer empleado',
    status: 'Estado',
    nobodyYet: 'Aquí todavía no trabaja nadie',
    nobodyYetHint: 'Contratar desde una plantilla te da un empleado cuya ficha ya es publicable.',
    level: 'nivel {level}',
    unpublishedCard: 'Ficha sin publicar',
    notFound: 'No existe ese empleado',
    notFoundHint: 'Puede que se le haya dado de baja, o que el enlace no sea el correcto.',
    backToEmployees: 'Volver a empleados',
    sections: 'Secciones del empleado',
    neverPublished: 'Nunca publicada',
    runningVersion: 'Versión {version} en marcha',
    dirtyDraft: 'el borrador tiene cambios sin publicar',
    tabs: {
      profile: 'Ficha',
      prompt: 'Prompt compilado',
      versions: 'Versiones',
      capabilities: 'Capacidades y accesos',
    },
    statuses: {
      active: 'Activo',
      pending_approval: 'Pendiente de aprobación',
      paused: 'En pausa',
      suspended: 'Suspendido',
      terminated: 'Dado de baja',
    },
    hire: {
      title: 'Contratar desde una plantilla',
      name: 'Nombre',
      nameHint: 'El slug sale del nombre. Si ya está en uso, se le añade un número.',
      department: 'Departamento',
      homeTeam: 'Equipo de origen',
      reportsTo: 'Reporta a',
      nobodyTop: 'Nadie: cabeza de la empresa',
      ownTemplate: 'de esta empresa',
      tokens: '~{count} tokens',
      submit: 'Contratar',
    },
  },

  profile: {
    steps: 'Pasos de la ficha',
    stepOf: 'Paso {current} de {total} · {label}',
    saveNow: 'Guardar ahora',
    noFindings: 'Sin errores ni avisos.',
    save: {
      saving: 'Guardando…',
      failed: 'No se ha podido guardar',
      dirty: 'Cambios sin guardar',
      saved: 'Guardado',
      upToDate: 'Al día',
    },
    findings: {
      errors: { one: '{count} error', other: '{count} errores' },
      warnings: { one: '{count} aviso', other: '{count} avisos' },
      errorsLabel: 'Errores',
      warningsLabel: 'Avisos',
      suggested: 'Sugerido: {text}',
    },
  },

  prompt: {
    invalidDraft: 'No hay prompt que enseñar mientras el borrador tenga errores: la misma respuesta que daría publicar.',
    estimatedTokens: 'tokens estimados',
    meta: '{hash} · compilador {compiler} · estimador {estimator}',
    breakdown: 'Desglose de tokens por sección',
  },

  versions: {
    publish: 'Publicar',
    publishHint:
      'Publicar valida el borrador, lo compila y guarda el resultado como una versión que después nada puede editar.',
    changeNote: 'Nota de cambio',
    changeNotePlaceholder: 'Opcional. Qué ha cambiado y por qué.',
    unchanged:
      'El borrador difiere de la versión {version}, pero compila al mismo prompt, así que no hay nada nuevo que publicar. El borrador sigue marcado como cambiado, porque de verdad ha cambiado.',
    nothingPublished: 'Todavía no se ha publicado nada',
    nothingPublishedHint:
      'Hasta que una ficha no se publica no hay ninguna versión a la que una ejecución de tarea pueda referirse.',
    publishedVersions: 'Versiones publicadas',
    version: 'Versión {version}',
    current: 'en uso',
    noNote: 'Sin nota',
    tokens: '{count} tokens',
    compare: 'Comparar',
    compareLabel: 'Comparar la versión {version} con la actual',
    revert: 'Revertir',
    revertLabel: 'Revertir a la versión {version}',
    diffLabel: 'Diferencias entre las versiones {a} y {b}',
    diffTitle: 'Versión {a} → {b}',
    whatChangedProfile: 'Qué ha cambiado en la ficha',
    whatChangedPrompt: 'Qué ha cambiado en el prompt',
    nothing: 'Nada.',
    promptIdentical: 'El prompt compilado es idéntico.',
  },

  profileSteps: {
    identity: {
      label: 'Identidad',
      mission: 'Misión',
      missionHint: 'Una o dos frases. Es lo primero que lee el modelo.',
      responsibilities: 'Responsabilidades',
      responsibilitiesHint:
        'De qué responde este empleado. Se pintan literalmente como viñetas, en este orden.',
      objectives: 'Objetivos',
      objectivesHint: 'Resultados, no actividades. Puede estar vacío.',
      kpis: 'Indicadores clave',
      kpisHint:
        'El objetivo se queda en texto («<= 3 USD») a propósito: convertirlo en número metería formateo de coma flotante dentro de un compilador que tiene que dar el mismo byte cada vez.',
      indicatorName: 'Nombre del indicador {number}',
      indicatorTarget: 'Objetivo del indicador {number}',
      removeIndicator: 'Quitar el indicador {number}',
      addIndicator: 'Añadir indicador',
      tone: 'Tono',
      verbosity: 'Extensión',
      verbosityHint: 'Escueto y conciso añaden además el bloque de concisión al prompt.',
      language: 'Idioma',
      languageHint: 'En qué escribe. El prompt en sí siempre va en inglés.',
      languageInherit: 'Heredar (idioma de la empresa)',
      styleNotes: 'Notas de estilo',
      personaNotes: 'Notas de personaje',
      personaNotesHint:
        'Trasfondo y sesgos productivos. Se pintan literalmente: el carácter es lo que establece la primera sección.',
    },

    capabilities: {
      label: 'Capacidades',
      model: 'Modelo',
      modelOption: '{name} · {price} USD/Mtok de salida',
      modelDeprecated: '{model} (DEPRECATED)',
      reasoningEffort: 'Esfuerzo de razonamiento',
      thinking: 'Razonamiento',
      thinkingHint: 'Desactivarlo con esfuerzo xhigh o max lo rechaza el proveedor.',
      maxOutputTokens: 'Tokens máximos de salida',
      maxTurns: 'Turnos máximos',
      whyThisModel: 'Por qué este modelo',
      whyThisModelHint: 'Una nota para ti, no para el modelo: nunca llega al prompt.',
      primaryStack: 'Pila principal',
      commaSeparated: 'Separadas por comas.',
      secondaryStack: 'Pila secundaria',
      domains: 'Dominios',
      seniorityContext: 'Contexto de seniority',
      tools: 'Herramientas',
      toolsHint:
        'Las aplica el sandbox, no el prompt. `bash` es la peligrosa y siempre lleva una política.',
      skills: 'Competencias',
      skillsHint:
        'Slugs de competencias de la empresa. Su contenido se inyecta literalmente en el prompt.',
      allowSubagents: 'Permitir subagentes',
      allowSubagentsHint: 'Los modelos actuales delegan con alegría; sin tope, el coste se multiplica.',
      maxConcurrent: 'Subagentes simultáneos máximos',
    },

    rules: {
      label: 'Reglas',
      hardRules: 'Reglas duras',
      hardRulesHint:
        'Cada regla debería llevar su motivo. Una prohibición sin él se aplica mal y en los contextos equivocados.',
      standardsReference: 'Referencia de estándares de código',
      standardsNotes: 'Notas de estándares de código',
      commitConvention: 'Convención de commits',
      commitConventionHint: '`conventional` se pinta como una frase completa en el prompt.',
      branchConvention: 'Convención de ramas',
      definitionOfDone: 'Definición de hecho',
      escalationTriggers: 'Disparadores de escalado',
      escalationTriggersHint:
        'Condiciones concretas. Sin disparadores, la sección se deja fuera del prompt por completo.',
      escalateTo: 'Escalar a',
      escalateToHint: 'Un selector: self.manager, employee:nora, team:backend.',
      fallbackToHuman: 'Recurrir a una persona',
      fallbackToHumanHint: 'Cuando no hay a quién escalar, abre una aprobación para ti.',
      maxCostPerTask: 'Coste máximo por tarea (micro-USD)',
      maxCostPerTaskHint: 'Millonésimas de dólar. 2.000.000 son 2 USD.',
      maxSecondsPerRun: 'Segundos máximos por ejecución',
      maxFilesChanged: 'Ficheros cambiados como máximo',
      maxLinesChanged: 'Líneas cambiadas como máximo',
    },

    context: {
      label: 'Contexto',
      knowledgeBases: 'Bases de conocimiento',
      knowledgeBasesHint: 'Slugs. Se empiezan a consultar en la fase 4.',
      memory: 'Memoria',
      rememberAcrossTasks: 'Recordar entre tareas',
      scope: 'Alcance',
      scopeHint: 'A escala de empresa se comparte una sola memoria entre todos; el ruido escala con ella.',
      itemsInContext: 'Elementos en contexto',
      retentionDays: 'Retención (días)',
      whatPromptPullsIn: 'Qué se trae el prompt',
      companyMission: 'Misión de la empresa',
      departmentMission: 'Misión del departamento',
      orgChartSummary: 'Resumen del organigrama',
      repositoryConventions: 'Convenciones del repositorio',
      recentTasks: 'Tareas recientes que resumir',
      recentTasksHint: 'Cero lo desactiva. Empieza a tener efecto en la fase 4.',
      projectAccessNote:
        'El acceso a proyectos está vacío hasta la fase 5, cuando existan proyectos. El esquema rechaza un valor aquí en lugar de aceptar uno sobre el que nadie puede actuar.',
    },

    enums: {
      tone: {
        direct: 'directo',
        collaborative: 'colaborativo',
        formal: 'formal',
        mentoring: 'de mentoría',
        analytical: 'analítico',
      },
      verbosity: {
        terse: 'escueto',
        concise: 'conciso',
        balanced: 'equilibrado',
        detailed: 'detallado',
      },
      memoryScope: {
        employee: 'empleado',
        team: 'equipo',
        company: 'empresa',
      },
      thinking: {
        adaptive: 'adaptativo',
        disabled: 'desactivado',
      },
      effort: {
        low: 'bajo',
        medium: 'medio',
        high: 'alto',
        xhigh: 'muy alto',
        max: 'máximo',
      },
    },
  },

  employeeWizard: {
    title: 'Empleado nuevo · paso 1 de 4',
    intro:
      'Quién es y dónde encaja. Los tres pasos siguientes escriben la ficha que se convierte en su prompt de sistema.',
    name: 'Nombre',
    slug: 'Slug',
    slugHint: 'Su dirección en el bus de mensajes y dentro de cada selector. No se puede cambiar después.',
    jobTitle: 'Puesto',
    seniority: 'Seniority',
    department: 'Departamento',
    homeTeam: 'Equipo de origen',
    reportsTo: 'Reporta a',
    nobodyTop: 'Nadie: cabeza de la empresa',
    submit: 'Crear y continuar',
    seniorities: {
      junior: 'Junior',
      mid: 'Intermedio',
      senior: 'Senior',
      lead: 'Lead',
      principal: 'Principal',
      executive: 'Directivo',
    },
  },

  org: {
    departments: {
      title: 'Departamentos',
      intro: 'Los departamentos contienen equipos y empleados. Un departamento puede estar dentro de otro.',
      add: 'Añadir un departamento',
      name: 'Nombre',
      slug: 'Slug',
      slugHint: 'Minúsculas, dígitos y guiones. No se puede cambiar después.',
      mission: 'Misión',
      missionHint: 'Opcional. Llega al prompt compilado de todos los de este departamento.',
      inside: 'Dentro de',
      topLevel: 'Primer nivel',
      empty: 'Aún no hay departamentos',
      emptyHint:
        'Los departamentos son opcionales, pero son lo que usa el prompt compilado para decirle a un empleado dónde encaja.',
      employees: { one: '{count} empleado', other: '{count} empleados' },
      archiveLabel: 'Archivar {name}',
    },

    teams: {
      title: 'Equipos',
      intro: 'Un empleado tiene un equipo de origen y puede pertenecer a varios más.',
      add: 'Añadir un equipo',
      name: 'Nombre',
      slug: 'Slug',
      slugHint: 'Minúsculas, dígitos y guiones.',
      type: 'Tipo',
      purpose: 'Propósito',
      department: 'Departamento',
      empty: 'Aún no hay equipos',
      emptyHint:
        'Los equipos son la forma de agrupar empleados para delegar y para las políticas de comunicación.',
      members: { one: '{count} miembro', other: '{count} miembros' },
      membersButton: 'Miembros',
      membersLabel: 'Miembros de {name}',
      archiveLabel: 'Archivar {name}',
      nobodyYet: 'Todavía no hay nadie en este equipo.',
      addEmployee: 'Añadir un empleado',
      choose: 'Elige…',
      role: 'Rol',
      removeLabel: 'Quitar a {name} de {team}',
      types: {
        functional: { label: 'Funcional', hint: 'Un equipo permanente: backend, diseño, soporte.' },
        project: { label: 'De proyecto', hint: 'Se forma para un proyecto y se disuelve con él.' },
        communication: { label: 'De comunicación', hint: 'El único tipo que puede hablar con otra empresa.' },
      },
      roles: {
        member: 'Miembro',
        lead: 'Responsable',
      },
    },

    chart: {
      title: 'Organigrama',
      intro: 'Arrastra un empleado sobre otro para que pase a ser su responsable. Todo el nivel de abajo se mueve con él.',
      empty: 'Aquí todavía no trabaja nadie',
      emptyHint: 'Contrata a tu primer empleado desde una plantilla y aparecerá aquí.',
      goToEmployees: 'Ir a empleados',
      label: 'Organigrama',
      nodeLabel: '{name}, {jobTitle}, nivel {level}',
      levelShort: 'Nivel {level}',
      cycleRefused: '{name} ya reporta a este empleado, directamente o a través de alguien.',
      cycleRefusedGeneric: 'Eso pondría a alguien en su propia cadena de mando.',
      thatEmployee: 'ese empleado',
      move: 'Mover a',
      toReportTo: 'Para que reporte a',
      nobodyTop: 'Nadie: cabeza de la empresa',
      choose: 'Elige…',
      reassign: 'Reasignar',
      levelCount: 'Nivel {level}:',
    },

    readiness: {
      title: 'Verificador de configuración',
      fixThis: 'Arreglar',
      fixThisLabel: 'Proponer arreglos para {check}',
      intro:
        'Lo mismo que el orquestador comprobará al despachar, comprobado ahora: cadena de mando, fichas publicadas, lead del proyecto, miembros, capacidades y repositorio. En verde, una tarea puede recorrer su pipeline de inicio a fin.',
    },
  },

  permissions: {
    title: 'Permisos',
    intro:
      'Quién puede hablar con quién, y por qué. Aquí nada se aplica hasta que se guarda, y ni la matriz ni el simulador escriben nada.',
    sections: 'Secciones de permisos',
    tabs: {
      matrix: 'Matriz',
      policies: 'Políticas',
      simulator: 'Simulador',
    },
    outcomes: {
      allow: 'Permitido',
      deny: 'Denegado',
      pending_approval: 'Necesita aprobación',
    },
    channels: {
      direct: { label: 'Directo', hint: 'Uno a uno.' },
      thread: { label: 'Hilo', hint: 'Unirse a una conversación que ya existe.' },
      broadcast: { label: 'Difusión', hint: 'A un grupo. Necesita además la capacidad message.broadcast.' },
      delegation: { label: 'Delegación', hint: 'Entregar una tarea. Necesita además task.delegate.' },
      escalation: { label: 'Escalado', hint: 'Hacia arriba en la cadena de mando. Ninguna política puede cerrar este.' },
    },
    matrix: {
      channel: 'Canal',
      groupBy: 'Agrupar por',
      nothing: 'Nada',
      team: 'Equipo',
      department: 'Departamento',
      counts: '{allowed} permitidos · {denied} denegados de {evaluated} pares',
      notEnough: 'No hay suficiente gente para una matriz',
      notEnoughHint: 'Contrata a un segundo empleado y esta rejilla enseñará quién puede hablar con quién.',
      legend:
        'Las filas son emisores, las columnas destinatarios. Pulsa una celda para ver qué política lo decidió.',
      caption:
        'Quién puede hablar con quién en el canal {channel}. Las filas son emisores, las columnas destinatarios.',
      axes: 'Emisor ↓ / Destinatario →',
      themselves: 'Sí mismo',
      cellTitle: '{reason} — abrir el simulador',
      cellLabel: '{sender} a {recipient}: {outcome}. Abrir el simulador.',
    },
  },

  policies: {
    order: 'Ordenadas como las clasifica el motor: primero la más específica, luego la prioridad, y luego denegar antes que permitir.',
    newPolicy: 'Política nueva',
    empty: 'Esta empresa no tiene políticas',
    emptyHint:
      'No hay nada escrito, así que el valor por defecto de la empresa lo decide todo. Denegar por defecto es seguro y completamente silencioso: una política es lo que le da al simulador algo que señalar.',
    builtIn: 'de serie',
    off: 'apagada',
    specificity: 'especificidad {value}',
    priority: 'prioridad {value}',
    conditional: 'condicional ({keys})',
    disable: 'Desactivar',
    enable: 'Activar',
    form: {
      systemNote:
        'Una de las ocho políticas con las que se crea toda empresa. Se puede editar y desactivar, no borrar, porque una empresa cuyo comportamiento no tiene causa visible es más difícil de arreglar que una con una regla apagada.',
      name: 'Nombre',
      nameHint: 'Lo que un gerente leerá en la lista, y lo que un mensaje de denegación le citará a un agente.',
      subject: 'De quién trata (sujeto)',
      target: 'A quién puede alcanzar (destino)',
      effect: 'Efecto',
      allow: 'Permitir',
      deny: 'Denegar',
      channels: 'Canales',
      channelsHint: 'Una política cubre lo que nombra y nada más.',
      conditions: 'Condiciones',
      conditionsHint:
        'Las cinco que aplica el motor (`05 §3.7`). Cualquier cosa que fuese a ignorar se rechaza en vez de guardarse.',
      holdForApproval: 'Retener para aprobación',
      holdForApprovalHint: 'El mensaje espera a un gerente. Ni se entrega ni se rechaza.',
      businessHours: 'Solo en horario laboral',
      businessHoursHint: 'De lunes a viernes, de 09:00 a 19:00 en la zona horaria de la empresa.',
      maxPerDay: 'Mensajes al día, por par',
      maxPerDayHint: 'Déjalo vacío para no poner límite.',
      minPriority: 'Prioridad mínima',
      any: 'Cualquiera',
      topics: 'Temas',
      topicsHint: 'Separados por comas. Si se rellena, el mensaje tiene que declarar uno de ellos.',
      priority: 'Prioridad',
      priorityHint:
        'Rompe el empate entre políticas de igual especificidad. A igual prioridad también, gana denegar.',
      messagePriority: {
        low: 'baja',
        normal: 'normal',
        high: 'alta',
        critical: 'crítica',
      },
    },
    selector: {
      placeholder: 'team:backend, level:<=1, self.manager, *',
      atCharacter: ' (en el carácter {offset})',
      specificity: 'Especificidad {value}',
      relative: 'relativo al emisor: a quién selecciona depende de quién pregunte',
      selects: { one: 'selecciona a {count} persona', other: 'selecciona a {count} personas' },
      unknownTerms: '{terms}: no existe nada con ese nombre todavía, así que esta parte no selecciona a nadie.',
    },
  },

  simulator: {
    sender: 'Emisor',
    recipient: 'Destinatario',
    channel: 'Canal',
    pickBoth: 'Elige un emisor y un destinatario.',
    theSender: 'El emisor',
    decidedBy: 'Política que decide',
    noPolicyMatched:
      'No ha coincidido ninguna política. Ha decidido el valor por defecto de la empresa, que es un arreglo distinto al de una regla que dice que no.',
    alsoMatched: 'Otras coincidencias (perdieron)',
    whatCanDo: 'Qué puede hacer {name}',
    toAllowIt: 'Para permitirlo',
    createIt: 'Crearla',
    disableIt: 'Desactivarla',
    generatedPolicyName: 'Permitir {subject} → {target}',
  },

  capabilities: {
    intro: 'Qué puede hacer este empleado. Una restricción ausente significa {noLimit}.',
    noLimit: 'sin límite',
    grant: 'Conceder una capacidad',
    empty: 'Todavía no hay capacidades',
    emptyHint:
      'Sin ninguna, este empleado no puede actuar en absoluto: ni siquiera enviar un mensaje. Concede lo que el puesto necesita y nada más.',
    limits: 'Límites',
    revoke: 'Revocar',
    capability: 'Capacidad',
    grantSubmit: 'Conceder',
    saveLimits: 'Guardar los límites',
    noLimits: 'Sin límites: la capacidad entera.',
    takesNoLimits: 'Esta capacidad no admite límites: se concede entera o no se concede.',
    invalidJson: 'Eso no es JSON válido.',
  },

  projects: {
    title: 'Proyectos',
    intro:
      'Un proyecto tiene repositorios, personal y presupuesto. Su slug pasa a formar parte del nombre de cada repositorio que crea.',
    newProject: 'Proyecto nuevo',
    showArchived: 'Ver los archivados',
    empty: 'Aún no hay proyectos',
    emptyHint: 'Un proyecto es donde viven los repositorios, el personal y el presupuesto. Crea uno antes de conectar código.',
    createFirst: 'Crea tu primer proyecto',
    notFound: 'No existe ese proyecto',
    notFoundHint: 'Puede que se haya archivado, o que el enlace no sea el correcto.',
    backToProjects: 'Volver a proyectos',
    status: {
      planning: 'Planificación',
      active: 'Activo',
      paused: 'En pausa',
      archived: 'Archivado',
    },
    tabs: {
      kubernetes: 'Kubernetes',
      jenkins: 'Jenkins',
      feedback: 'Feedback',
      overview: 'Resumen',
      repositories: 'Repositorios',
      members: 'Personal',
    },
    filters: {
      search: 'Buscar proyecto',
      searchHint: 'Por nombre, slug o pila tecnológica.',
      clearSearch: 'Borrar la búsqueda',
      status: 'Estado',
      anyStatus: 'Cualquier estado',
      kubernetes: 'Kubernetes',
      jenkins: 'Jenkins',
      anyIntegration: 'Cualquiera',
      clear: 'Quitar los filtros',
      showing: '{shown} de {total} proyectos',
      noMatch: 'Ningún proyecto coincide',
      noMatchHint: 'Prueba con otra búsqueda o quita algún filtro.',
    },
    table: {
      project: 'Proyecto',
      status: 'Estado',
      lead: 'Responsable',
      tasks: 'Tareas',
      tasksHint: 'Abiertas / total',
      lastRequested: 'Última solicitada',
      lastFinished: 'Última finalizada',
      repositories: 'Repos',
      members: 'Personal',
      kubernetes: 'Kubernetes',
      jenkins: 'Jenkins',
      never: 'Nunca',
      sortBy: 'Ordenar por {column}',
      openTab: 'Abrir la pestaña de {tab}',
    },
    integration: {
      configured: 'Configurado',
      not_configured: 'Sin configurar',
      no_connection: 'Sin conexión',
      noConnectionHint: 'La empresa no tiene esta integración conectada; configurarla en el proyecto no basta.',
      namespaces: '{count} namespaces',
    },
    form: {
      name: 'Nombre',
      slug: 'Slug',
      slugHint:
        'Minúsculas y guiones. Es el {project} de los nombres de tus repositorios, y no se puede cambiar después.',
      description: 'Descripción',
      goals: 'Objetivos',
      techStack: 'Pila tecnológica',
      techStackHint:
        'Separada por comas. Decide la imagen del sandbox y qué puede contener su lista de salida permitida.',
      status: 'Estado',
      submit: 'Crear el proyecto',
    },
    overview: {
      techStackHint:
        'En qué está construido este proyecto. Decide la imagen del sandbox y qué puede contener su lista de salida permitida.',
      save: 'Guardar el proyecto',
      github: 'Comportamiento de GitHub',
      mirrorIssues: 'Reflejar las tareas en GitHub Issues',
      mirrorIssuesHint:
        'En un solo sentido, y desactivado por defecto: esto es el sistema escribiendo en tu repositorio por iniciativa propia.',
      allowedCommenters: 'Comentaristas permitidos',
      allowedCommentersHint:
        'Logins de GitHub que pueden darle órdenes a @absorbot. Vacío significa nadie: el cuerpo de un comentario es entrada no fiable, y esta lista es lo que hay entre él y un presupuesto cambiado.',
      egressHosts: 'Hosts de salida adicionales',
      egressHostsHint:
        'Nombres de host que los sandboxes de este proyecto pueden alcanzar además de lo que su pila justifica. Solo nombres de host: una URL o un CIDR aquí no casa con nada, en silencio.',
      saveGithub: 'Guardar el comportamiento de GitHub',
      archive: 'Archivar',
      archiveHint: 'Archivar conserva el histórico y los costes. En GitHub no se borra nada.',
      archiveProject: 'Archivar el proyecto',
      archiveConfirm: 'Sus repositorios siguen conectados, pero no se programa trabajo nuevo contra él.',
    },
  },

  repositories: {
    title: 'Repositorios',
    intro: 'Solo privados. Un repositorio que se vuelve público se desconecta la próxima vez que miramos (caso G-12).',
    connectExisting: 'Conectar uno existente',
    createNew: 'Crear uno nuevo',
    noCredential:
      'Esta empresa todavía no tiene una credencial de GitHub utilizable. Añade un PAT de grano fino o instala la App en {where} antes de conectar código.',
    settingsGithub: 'Ajustes → GitHub',
    empty: 'Aún no hay repositorios',
    emptyHint:
      'Un agente sin repositorio trabaja sobre un volumen vacío. Conecta o crea uno para darle código a este proyecto.',
    inactive: '{count} inactivos ({statuses})',
    pat: {
      title: 'Esta empresa llega a GitHub con un token de acceso personal.',
      body:
        'Los tokens no se pueden recortar por ejecución, así que todo agente que trabaje aquí recibe el mismo acceso que tenga el token. Instalar la App de GitHub es lo que hace que el token de un empleado coincida con sus capacidades.',
    },
    connect: {
      fullName: 'Nombre completo',
      fullNameHint: 'Tiene que ser privado. Un repositorio público se rechaza antes de escribir nada (caso G-18).',
      role: 'Rol',
      roleHint: 'Qué es este repositorio dentro del proyecto. Opcional.',
      branchProtection: 'Aplicar protección de rama',
      branchProtectionHint:
        'Al mejor esfuerzo: un PAT de grano fino normalmente no puede ponerla, y que falle no es un error.',
      skillsWarning:
        'La carpeta {folder} del repositorio la carga como instrucciones cualquier agente que trabaje en él. Si este repositorio tiene colaboradores externos, mira qué contiene esa carpeta antes de conectarlo (M-9).',
      submit: 'Conectar',
    },
    create: {
      component: 'Componente',
      componentHint: 'El {repo} de la plantilla de nombres de la empresa. El resto del nombre se decide solo.',
      previewEmpty: 'El nombre completo aparece aquí según escribes.',
      previewChecking: 'Comprobando…',
      previewError:
        'Ahora mismo no se puede previsualizar el nombre. Crearlo te dirá igualmente qué ha ido mal exactamente.',
      unusable: 'Inutilizable: {violations}',
      free:
        'Libre en el momento en que preguntamos. El nombre solo es tuyo cuando queda reservado, así que una carrera aún puede empujarte al -2.',
      taken: 'Ocupado. Al crearlo se recurrirá al siguiente sufijo libre.',
      description: 'Descripción',
      submit: 'Crear en GitHub',
    },
    card: {
      synced: 'sincronizado {date}',
      settingsTab: 'Ajustes y comandos',
      conventionsTab: 'Convenciones',
      commands: 'Comandos',
      detect: 'Detectar desde el repositorio',
      detectHint:
        'La detección solo propone lo que puede demostrar que existe: un package.json sin script {lint} nunca se convierte en {npmLint}. Una casilla vacía significa que no hay ninguno.',
      branchPrefix: 'Prefijo de rama',
      branchPrefixHint: 'Sobrescribe el prefijo del proyecto para este repositorio.',
      mergeStrategy: 'Estrategia de fusión',
      squash: 'Squash',
      mergeCommit: 'Commit de fusión',
      rebase: 'Rebase',
      commitConvention: 'Convención de commits',
      conventional: 'Conventional commits',
      plain: 'Sin convención',
      maxFiles: 'Ficheros cambiados como máximo para auto-fusión',
      maxLines: 'Líneas cambiadas como máximo para auto-fusión',
      protectedPaths: 'Rutas protegidas',
      protectedPathsHint:
        'Globs que un agente no puede cambiar sin aprobación. Se aplican dos veces: un hook dentro del sandbox y la compuerta de fusión.',
      ciChecks: 'Comprobaciones de CI obligatorias',
      ciChecksHint: 'Nombres de check de GitHub, comparados literalmente con lo que reporta check_suite.',
      requireCi: 'Exigir que CI esté en verde',
      deleteBranch: 'Borrar la rama al fusionar',
      agrees: 'GitHub coincide con lo que tenemos.',
      reconciled: 'Reconciliado: {changes}.',
      saveSettings: 'Guardar los ajustes',
      sync: 'Sincronizar con GitHub',
      disconnect: 'Desconectar',
      disconnectConfirm:
        'En GitHub no se borra nada y la fila conserva su histórico. Los agentes dejan de poder alcanzarlo.',
      status: {
        active: 'activo',
        creating: 'creándose',
        archived: 'archivado',
        disconnected: 'desconectado',
      },
    },
  },

  members: {
    title: 'Personal',
    intro:
      'Quién trabaja en este proyecto y en qué rol. El enrutador de tareas lee estos roles cuando elige a un responsable.',
    empty: 'Aquí todavía no hay nadie asignado',
    emptyHint: 'Añade un empleado para que se le puedan asignar tareas.',
    employee: 'Empleado',
    choose: 'Elige…',
    role: 'Rol',
    allocation: 'Dedicación %',
    add: 'Añadir',
    assist: 'Asignar con el asistente',
    access: 'Acceso',
    allRepos: 'todos los repos',
    someRepos: '{count} repos',
    noAccess: 'sin acceso',
    roles: {
      lead: 'Responsable',
      architect: 'Arquitecto',
      developer: 'Desarrollador',
      reviewer: 'Revisor',
      qa: 'QA',
      designer: 'Diseñador',
      devops: 'DevOps',
      observer: 'Observador',
    },
  },

  conventions: {
    nothingToPreview: 'Todavía no hay nada que previsualizar.',
    versionLine: 'Versión {version} · {source}',
    sourceGenerated: 'generado',
    sourceManual: 'escrito a mano',
    empty:
      'Todavía no hay documento de convenciones. Lo que escribas aquí se le entrega a todos los agentes que trabajen en este repositorio.',
    edit: 'Editar',
    preview: 'Previsualizar',
    regenerate: 'Regenerar desde el código',
    regenerateQueued: 'Regeneración encolada.',
    regenerateFailed: 'Ahora mismo no se puede pedir la regeneración.',
    label: 'Documento de convenciones',
    placeholder:
      '# Convenciones\n\nCómo se nombran las ramas en este repositorio, cómo se escriben los commits y cómo se estructuran los cambios.',
    budget:
      '{used} / {max} caracteres. Este texto se inyecta en un prompt de sistema, y su presupuesto de tokens es el recurso más escaso que tiene un empleado.',
    save: 'Guardar como versión nueva',
    discard: 'Descartar los cambios',
    versionCount: '{count} versiones',
    load: 'Cargar en el editor',
  },

  costs: {
    title: 'Costes',
    previousMonth: 'Mes anterior',
    nextMonth: 'Mes siguiente',
    estimatedNote:
      'Esta empresa usa credenciales de suscripción. Los importes son estimaciones a precio de lista y no se corresponden con la facturación.',
    spentThisMonth: 'Gastado este mes',
    budget: 'Presupuesto',
    noBudget: 'No hay presupuesto.',
    createOne: 'Crea uno',
    remaining: '{amount} restantes · {percent} usado · al 100%: {policy}',
    projected: 'Proyección a fin de mes',
    atCurrentPace: 'Al ritmo actual.',
    dailySpend: 'Gasto diario',
    noSpend: 'No hay gasto registrado en este periodo.',
    nothingInPeriod: 'Nada en este periodo.',
    others: 'otros ({count})',
    alertAt: 'Aviso al {percent}',
    byProject: 'Por proyecto',
    byEmployee: 'Por empleado',
    byModel: 'Por modelo',
    byTaskType: 'Por tipo de tarea',
    dailySpendLabel: 'Gasto diario',
    calls: '{count} llamadas',
    efficiency: 'Eficiencia',
    avgCostPer: 'Coste medio por {type}',
    retryCost: 'Coste de reintentos',
    ofTotal: '{percent} del total',
    reviewCost: 'Coste del ciclo de revisión',
    cacheRatio: 'Aciertos de caché de prompt',
    healthy: 'sano',
    low: 'bajo',
    discarded: 'Trabajo descartado',
    tokensPerFile: 'Tokens por fichero cambiado',
    employee: 'Empleado',
    avgPerTask: 'Media por tarea',
    p95: 'p95',
    tasks: 'Tareas',
    budgetStatus: {
      ok: 'correcto',
      warning: 'aviso',
      exhausted: 'agotado',
    },
    policy: {
      block_new: 'bloquear trabajo nuevo',
      block_all: 'parar todo',
      warn_only: 'solo avisar',
    },
  },

  budgets: {
    title: 'Presupuestos',
    newBudget: 'Nuevo presupuesto',
    createTitle: 'Presupuesto nuevo',
    empty: 'Aún no hay presupuestos',
    emptyHint:
      'Sin presupuesto, el gasto solo está limitado por tarea. Un presupuesto mensual de empresa es la primera barandilla habitual.',
    createOne: 'Crear un presupuesto',
    periodShort: {
      monthly: 'mensual',
      total: 'total',
    },
    scope: 'Ámbito',
    wholeCompany: 'Toda la empresa',
    oneProject: 'Un proyecto',
    oneEmployee: 'Un empleado',
    choose: 'Elige…',
    period: 'Periodo',
    monthly: 'Mensual: se reinicia el día 1',
    total: 'Total: un único bote, nunca se reinicia',
    limit: 'Límite (USD)',
    create: 'Crear el presupuesto',
    invalidAmount:
      'Introduce un importe como 25 o 12,50 (hasta seis decimales). 0 congela el ámbito.',
    chooseScope: 'Elige qué {scope} cubre este presupuesto.',
    thresholds: 'Umbrales de aviso',
    removeThreshold: 'Quitar el umbral del {percent}',
    addThreshold: 'Añadir un umbral (porcentaje)',
    thresholdsHint: 'Se lanza una notificación una vez por periodo cuando el gasto cruza cada marca.',
    whenExhausted: 'Cuando se agota el presupuesto',
    policyOption: '{key} — {label}',
    policyExplanation: {
      block_new: 'Por defecto. Las tareas en marcha terminan; no empieza nada nuevo.',
      block_all: 'Las ejecuciones en curso se cortan también; el trabajo que ya está en la rama se conserva.',
      warn_only: 'Solo notifica. Para entornos de prueba.',
    },
    atFull: 'al 100%: {policy}',
    usage: '{spent} de {limit} · {remaining} restantes · {percent} usado',
    close: 'Cerrar',
    deleteConfirm:
      'El presupuesto deja de aplicarse de inmediato. No se toca nada más: el gasto se sigue registrando, simplemente ya no hay límite en este ámbito.',
    fallbackCompany: 'Empresa',
    fallbackProject: 'Proyecto',
    fallbackEmployee: 'Empleado',
    concurrency: {
      title: 'Concurrencia',
      intro:
        'Cada sandbox se lleva unas 2 vCPU y 4 GB de RAM, así que un host pequeño se satura rápido: una cola visible es mejor que un servidor que se arrastra. Sube los límites midiendo. Una tarea más allá del límite de cola se queda bloqueada, con el motivo visible en su tarjeta.',
      ceiling:
        'Techo de la plataforma: {count} ejecuciones simultáneas entre todas las empresas, fijado por el operador y no editable aquí.',
      defaults: 'Esta empresa funciona con los valores por defecto (3 simultáneas, 200 en cola). Guardar escribe su propia fila.',
      maxRuns: 'Ejecuciones simultáneas máximas',
      maxQueued: 'Tareas en cola máximas',
      save: 'Guardar los límites',
    },
  },

  companyWizard: {
    title: 'Empresa nueva',
    intro:
      'Una empresa es la frontera de los datos, los permisos y el presupuesto. Todo lo de aquí se puede cambiar después menos el slug.',
    name: 'Nombre',
    slug: 'Slug',
    slugHint:
      'Minúsculas, dígitos y guiones simples. Pasa a formar parte de los nombres de tus repositorios en GitHub y no se puede cambiar después.',
    industry: 'Sector',
    industryHint: 'Enriquece el contexto con el que trabajan tus empleados.',
    mission: 'Misión',
    missionHint: 'Se inyecta en el prompt de sistema de todos los empleados de esta empresa.',
    submit: 'Crear la empresa',
  },

  companySettings: {
    workingLanguage: 'Idioma de trabajo',
    workingLanguageHint: 'El idioma en el que escriben tus empleados. No es el idioma de esta interfaz, que se elige en tu cuenta.',
    budgetLimits: 'Aplicar los topes de gasto',
    budgetLimitsHint:
      'Marcado, los límites paran el trabajo: el tope por tarea, el de la ficha del empleado, el de la empresa y los presupuestos por proyecto o persona. Desmarcado, no para nada y las tareas siguen aunque agoten su presupuesto. El gasto se sigue registrando igual.',
    timezone: 'Zona horaria',
    timezoneHint: 'Un identificador IANA, como Europe/Madrid.',
    slugFixed: 'Fijado al crearla: forma parte de los nombres de tus repositorios en GitHub.',
  },

  danger: {
    intro: 'Lo que para o revierte la empresa entera. Nada de aquí borra datos, pero todo se nota de inmediato.',
    archiveTitle: 'Archivar esta empresa',
    restoreTitle: 'Restaurar esta empresa',
    archiveHint:
      'Archivar detiene todo el trabajo y deja la empresa en solo lectura. No se borra nada —el histórico y los costes se conservan— y puedes restaurarla después.',
    restoreHint: 'Mientras está archivada es de solo lectura. Restaurarla la vuelve editable.',
    restore: 'Restaurar',
    archive: 'Archivar',
    confirm:
      'Todo el trabajo se detiene y la empresa pasa a solo lectura. No se borra nada, y puedes restaurarla después.',
    stopTitle: 'Parada de emergencia',
    stopHint:
      'Cancela ahora mismo todas las ejecuciones en marcha y deja la empresa en pausa: ningún empleado actúa y no arranca nada nuevo. Se deja de gastar en segundos. El trabajo ya comprometido en ramas se conserva.',
    stop: 'Parar todo ahora',
    stopConfirm: '¿Seguro? Se cancelará todo lo que esté ejecutándose.',
    stopConfirmYes: 'Sí, parar todo',
    stopped: '{count} ejecuciones canceladas. La empresa está en pausa.',
    pausedTitle: 'Empresa en pausa por parada de emergencia',
    pausedHint:
      'No arranca ninguna ejecución nueva y los empleados no pueden actuar. Al reactivar, las tareas que quedaron a medias vuelven al orquestador.',
    resume: 'Reactivar la empresa',
    resumed: 'Empresa reactivada. {count} tareas devueltas al orquestador.',
  },

  audit: {
    intro: 'Cada cambio de esta empresa, quién lo hizo y cuándo.',
    filterPlaceholder: 'Filtrar por acción, p. ej. company.settings_updated',
    entries: '{count} entradas',
    empty: 'Todavía no hay nada registrado',
    emptyFiltered: 'Ninguna entrada coincide con esa acción.',
    emptyHint: 'Aquí aparecerá cada cambio de esta empresa.',
    loadOlder: 'Cargar entradas más antiguas',
  },

  credentials: {
    rotate: 'Rotar API key',
    intro:
      'Las credenciales se cifran antes de guardarse y se comprueban primero contra el proveedor: una que no sea válida no llega a guardarse.',
    add: 'Añadir una credencial',
    type: 'Tipo',
    label: 'Etiqueta',
    labelPlaceholder: 'Cuenta principal',
    secret: 'Secreto',
    secretHint: 'Se guarda cifrado. No se vuelve a enseñar: solo los últimos caracteres.',
    useAsDefault: 'Usar como predeterminada de su tipo',
    checkAndSave: 'Comprobar y guardar',
    empty: 'Aún no hay credenciales',
    emptyHint:
      'Tus empleados necesitan una credencial del proveedor de su modelo para ejecutarse, y una de GitHub para tocar repositorios.',
    default: 'predeterminada',
    makeDefault: 'Hacer predeterminada',
    unsetDefault: 'Quitar de predeterminada',
    check: 'Comprobar',
    revoke: 'Revocar',
    accepted: 'El proveedor ha aceptado esta credencial.',
    rejected: 'El proveedor la ha rechazado: {reason}',
    noReason: 'sin motivo',
    unreachable: 'No se ha podido contactar con el proveedor, así que no ha cambiado nada. Inténtalo en un momento.',
    /**
     * Cada tipo se saca de un sitio distinto, se comprueba de una forma
     * distinta y cuesta de una forma distinta. Las tres cosas se deciden antes
     * de pegar el secreto, no después de que el proveedor conteste que no.
     */
    openai: {
      disconnect: 'Desconectar ChatGPT',
      title: "Suscripción de ChatGPT",
      intro: "Conecta una cuenta con acceso a Codex. El consumo usa los límites de su plan; los importes se muestran como estimaciones.",
      connection: "Conexión",
      new: "Nueva conexión",
      connect: "Conectar con ChatGPT",
      reconnect: "Reconectar con ChatGPT",
      authorize: "Autorizar en OpenAI",
      code: "Código:",
      fallback: "Permitir el otro modo como respaldo al iniciar una sesión. Puede generar consumo facturado por API.",
      states: {
        pending: "Esperando autorización…",
        connected: "Cuenta conectada.",
        cancelled: "Conexión cancelada.",
        expired: "El código ha caducado. Inicia otra conexión.",
        rejected: "OpenAI no autorizó la conexión.",
        temporary_error: "No se pudo completar la conexión. Vuelve a intentarlo.",
      },
    },
    guides: {
      openai_api_key: {
        what: "El consumo se factura por tokens a la cuenta de OpenAI que emite la clave.",
        step1: "Accede a platform.openai.com con la cuenta que paga.",
        step2: "Crea una API key en el proyecto correspondiente.",
        step3: "Copia y guarda la clave aquí.",
        shape: "sk-proj-…",
        note: "La API tiene facturación independiente de la suscripción de ChatGPT.",
      },
      shapeLabel: 'Tiene esta pinta:',
      anthropic_api_key: {
        what: 'La vía normal. El consumo se factura a la cuenta de Anthropic que emita la clave, y los importes que verás aquí son los medidos, no una estimación.',
        step1: 'Entra en console.anthropic.com con la cuenta que paga.',
        step2: 'En API keys, crea una clave dentro del workspace al que quieras que vaya el gasto.',
        step3: 'Cópiala en ese momento: la consola no la vuelve a enseñar entera.',
        shape: 'sk-ant-api03-…',
        note: 'Si esta empresa tiene a la vez clave y suscripción, tú eliges cuál gastan las ejecuciones; el asistente de fichas sigue la misma elección.',
      },
      claude_subscription_oauth: {
        what: 'Hace correr a tus empleados con tu suscripción de Claude —Pro o Max— en lugar de con saldo de API.',
        step1: 'Instala Claude Code e inicia sesión con la cuenta que tiene la suscripción.',
        step2: 'Ejecuta claude setup-token en tu terminal.',
        step3: 'Pega aquí, entero, el token que imprime.',
        shape: 'sk-ant-oat01-…',
        note: 'Dos consecuencias. El gasto pasa a ser una estimación a precio de lista —los importes salen con ~, porque una suscripción no factura por token— y el token caduca sin renovarse solo: cuando el proveedor lo rechace hay que generar otro y guardarlo aquí.',
      },
      github_pat: {
        what: 'El respaldo de la App de GitHub, para cuentas personales o mientras la App no esté instalada.',
        step1: 'En GitHub, Settings → Developer settings → Personal access tokens.',
        step2: 'De grano fino: Contents y Pull requests en lectura y escritura sobre los repositorios de esta empresa. Clásico: el scope repo entero, que es el que se comprueba al guardar.',
        step3: 'Copia el token antes de salir de esa página.',
        shape: 'github_pat_… · ghp_…',
        note: 'Un PAT no se puede recortar por ejecución: cualquier empleado recibe todo el acceso que tenga el token. Los de la App sí se recortan, y por eso es la vía preferida.',
      },
    },
    runPreference: {
      title: 'Con qué credencial trabajan tus empleados',
      intro: "Elige el modo preferido para este proveedor. El respaldo solo se utiliza si lo permites.",
      api_key: 'La clave de API.',
      api_keyHint: 'Coste real medido por token, y más ejecuciones en paralelo.',
      subscription: 'La suscripción.',
      subscriptionHint: 'Tarifa plana, sujeta a los límites de tu plan, y los importes salen como estimación a precio de lista.',
      assistant: "Esta elección se aplica a empleados y al asistente de fichas.",
    },
    types: {
      jenkins_api_token: 'Token API de Jenkins',
      kubeconfig: 'Kubeconfig',
      openai_api_key: "Clave de API de OpenAI",
      openai_subscription_oauth: "Suscripción de ChatGPT",
      anthropic_api_key: 'Clave de API de Anthropic',
      claude_subscription_oauth: 'Suscripción de Claude (OAuth)',
      github_pat: 'Token de acceso personal de GitHub',
    },
    status: {
      expired: 'caducada',
      active: 'activa',
      revoked: 'revocada',
      invalid: 'no válida',
      expiring: 'caducando',
    },
  },

  githubSettings: {
    title: 'GitHub',
    intro: 'Dónde vive el código de esta empresa, y cuánto de él puede alcanzar un agente concreto.',
    appConnected: 'La App de GitHub está instalada.',
    appExplanation:
      'Los tokens se emiten por ejecución y se recortan a las capacidades del empleado que corre: un empleado sin {capability} recibe un token al que el propio GitHub le deniega la fusión. Caducan en una hora y nunca tocan la credencial guardada de esta empresa.',
    noRepositories: 'La instalación no da acceso a ningún repositorio ahora mismo. Añade alguno desde GitHub para conectarlos aquí.',
    visibleRepositories: { one: '{count} repositorio visible para esta instalación.', other: '{count} repositorios visibles para esta instalación.' },
    patTitle: 'Esta empresa llega a GitHub con un token de acceso personal.',
    patPoints: {
      trim: 'Los tokens no se pueden recortar por ejecución: todo agente recibe el acceso que tenga el token.',
      webhooks: 'Los webhooks hay que registrarlos a mano, repositorio a repositorio, con el secreto de abajo.',
      protection:
        'La protección de rama normalmente no la puede poner un token de grano fino, y que falle no es un error.',
    },
    installInstead: 'Instalar la App de GitHub en su lugar',
    nothingTitle: 'Todavía no hay credencial de GitHub.',
    nothingHint:
      'Hasta que la haya, no se puede conectar ningún repositorio y los agentes corren contra un espacio de trabajo vacío. La App es la mejor mitad de la elección —es la única cuyos tokens se pueden recortar por ejecución— y un PAT de grano fino añadido en Credenciales funciona en todo lo que funciona la App, más ampliamente.',
    install: 'Instalar la App de GitHub',
    installed: 'La instalación ya está conectada a esta empresa.',
    finishing: 'Terminando la instalación…',
    webhook: 'Webhook',
    webhookIntro:
      'Con un PAT, hay que decirle a GitHub adónde mandar los eventos. Registra esta URL y este secreto en los ajustes de cada repositorio: las entregas cuya firma no verifique se responden con un 401 y quedan registradas.',
    payloadUrl: 'URL de destino',
    secretLabel: 'Secreto',
    contentType: 'Tipo de contenido {type}. Es el mismo secreto que ya tiene GitHub en cada repositorio donde esté registrado el webhook.',
    secretConfigured: 'Hay un secreto configurado. Se puede consultar y copiar cuando haga falta: registrar el webhook de un repositorio nuevo no exige rotarlo.',
    noSecret: 'Todavía no se ha generado ningún secreto.',
    reveal: 'Ver el secreto',
    rotate: 'Rotar el secreto',
    rotateWarning:
      'Rotarlo invalida el anterior: los repositorios que ya lo tengan registrado dejan de entregar hasta que se actualicen uno a uno.',
    generate: 'Generar un secreto',
  },

  generalSettings: {
    intro: 'La identidad de la empresa y cómo trabaja: nombre, misión, idioma de trabajo y zona horaria.',
  },

  reposSettings: {
    intro: 'Cómo se llaman los repositorios que crea la empresa y con qué valores ejecutan sus empleados si no dicen otra cosa.',
    template: 'Plantilla de nombres de repositorio',
    templateHint: 'Variables disponibles: {vars}. Tiene que contener {repo}.',
    wouldBeNamed: 'Un repositorio se llamaría',
    defaultModel: 'Modelo por defecto',
    defaultModelHint: 'Lo hereda todo empleado que no lo sobrescriba.',
    defaultEffort: 'Esfuerzo de razonamiento por defecto',
    alwaysPrivate: 'Los repositorios se crean siempre privados.',
  },

  autonomy: {
    title: 'Autonomía',
    intro: 'Qué pueden hacer los agentes sin una persona, y dónde siempre se pregunta a una.',
    sections: 'Secciones de autonomía',
    tabs: {
      company: 'Valor de empresa',
      projects: 'Excepciones por proyecto',
      simulator: 'Simulador',
    },
    noPolicy:
      'Todavía no hay ninguna política configurada: esto parte del documento recomendado de la especificación. Nada se aplica hasta que lo guardes.',
    policyEnabled: 'Política activa',
    policyEnabledHint:
      'Una política desactivada se salta y se aplica el siguiente ámbito; apagar una hace las cosas más estrictas, nunca más laxas.',
    savePolicy: 'Guardar la política',
    removeOverride: 'Quitar la excepción',
    saved: 'Guardada.',
    project: 'Proyecto',
    choose: 'Elige…',
    hasOverride: ' · tiene excepción',
    noOverride:
      'Un proyecto sin excepción sigue el valor por defecto de la empresa. Una política se aplica entera: no hay mezcla entre ámbitos.',
    riskLevel: {
      low: 'bajo',
      medium: 'medio',
      high: 'alto',
      critical: 'crítico',
    },
    rules: {
      planGate: 'Compuerta de plan',
      planGateHint:
        'Cuándo tiene que ver el plan una persona antes de que empiece la implementación. El plan es el último punto en el que una dirección equivocada cuesta una ejecución y no seis.',
      enabled: 'Activada',
      onTaskTypes: 'En estos tipos de tarea',
      onRiskLevels: 'En estos niveles de riesgo',
      autoMerge: 'Auto-fusión',
      autoMergeHint:
        'Las reglas de la compuerta de fusión (09 §5.3). Una PR que supera todas las comprobaciones se fusiona sola; cualquier otra pregunta a una persona, con el motivo nombrado.',
      autoMergeEnabledHint: 'Apagada, cada fusión pregunta a una persona. El resto de esta sección deja de importar.',
      requireCi: 'Exigir CI en verde',
      requireQa: 'Exigir aprobación del QA IA',
      peerReviews: 'Aprobaciones de revisión por pares',
      maxFiles: 'Ficheros cambiados como máximo',
      maxLines: 'Líneas cambiadas como máximo',
      maxRisk: 'Riesgo máximo que aún puede fusionarse solo',
      allowedTypes: 'Tipos de tarea permitidos',
      forbidIfTouches: 'Nunca fusionar sin supervisión si el diff toca',
      forbidIfTouchesHint:
        'Patrones glob, separados por comas. Distinto de las rutas protegidas: esto va de fusionar sin supervisión, no de cambiar en absoluto.',
      humanGates: 'Compuertas humanas',
      humanGatesHint: 'Las situaciones que siempre preguntan a una persona, diga lo que diga la compuerta de fusión.',
      onRepoCreate: 'Crear un repositorio',
      onDependencyChange: 'Cambiar dependencias',
      onDependencyChangeHint: 'Por donde llega un compromiso de la cadena de suministro.',
      onBudgetIncrease: 'Subir un presupuesto',
      onFirstTask: 'Primera tarea de un proyecto',
      retries: 'Reintentos',
      retriesHint:
        'Las protecciones de bucle de 09 §7.2. Una fase que puede repetirse sin techo es una factura sin techo.',
      attemptsPerPhase: 'Intentos por fase',
      interruptionsPerPhase: 'Interrupciones por fase',
      interruptionsPerPhaseHint:
        'Cuántas veces puede la plataforma tumbar una fase —un runtime que se muere, una sesión cortada por el tope de duración— antes de que tenga que mirarlo una persona. Una interrupción se continúa, no se repite, así que no gasta los intentos del agente.',
      phaseContinuations: 'Continuaciones por fase',
      phaseContinuationsHint:
        'Cuántas veces puede una sesión de implementación o de arreglo pararse en un punto de control —termina con la tarea en curso y lo dice— y continuarse en otra sesión en vez de ir a revisión. Al superarlo, la tarea se escala.',
      reviewCycles: 'Ciclos de revisión',
      qaCycles: 'Ciclos de QA',
      ciFixCycles: 'Ciclos de arreglo de CI',
      backoff: 'Segundos de espera',
      backoffHint: 'Una entrada por reintento, en orden, separadas por comas. Vacío significa sin espera.',
      escalation: 'Escalado',
      escalationHint: 'Cuándo dejar de intentarlo y avisar a alguien.',
      onRepeatedFailure: 'Ante fallo repetido',
      budgetThreshold: 'Umbral de aviso de presupuesto (0–1)',
      budgetThresholdHint:
        'Fracción del presupuesto de la tarea a la que se avisa al responsable. 0 lo desactiva.',
      stuckMinutes: 'Atascada tras (minutos)',
    },
    gate: {
      chooseProject: 'Elige un proyecto…',
      projectHint: 'Qué política se aplica depende del proyecto.',
      taskType: 'Tipo de tarea',
      risk: 'Riesgo',
      ci: 'CI',
      approvingReviews: 'Revisiones que aprueban',
      changedFiles: 'Ficheros cambiados',
      addedLines: 'Líneas añadidas',
      removedLines: 'Líneas eliminadas',
      changedPaths: 'Rutas cambiadas',
      changedPathsHint: 'Separadas por comas. Es lo que lee la comprobación de globs prohibidos.',
      qaApproved: 'QA aprobado',
      assigneeMayMerge: 'El responsable puede fusionar',
      budgetExhausted: 'Presupuesto agotado',
      pickProject: 'Elige un proyecto para ver qué decidiría la compuerta.',
      mergesItself: 'se fusiona sola',
      asksAPerson: 'pregunta a una persona',
      waits: 'espera',
      ciStatus: {
        success: 'en verde',
        failure: 'en rojo',
        pending: 'pendiente',
        not_required: 'no obligatoria',
      },
    },
  },

  agreements: {
    title: 'Acuerdos',
    propose: 'Proponer un acuerdo',
    empty: 'Aún no hay acuerdos',
    emptyHint:
      'Dos empresas colaboran únicamente a través de un acuerdo aceptado y de sus equipos de comunicación. Propón uno para abrir la puerta.',
    proposedToYou: 'te lo han propuesto',
    summary: '{title} · temas: {topics} · delegación: {delegation} · repos: {repos}',
    none: 'ninguno',
    nothing: 'nada',
    noneYet: 'ninguno todavía',
    yes: 'sí',
    no: 'no',
    reviewAndDecide: 'Revisar y decidir',
    view: 'Ver',
    proposeAction: 'Proponer',
    withdraw: 'Retirar',
    suspend: 'Suspender',
    resume: 'Reanudar',
    revoke: 'Revocar',
    revokeConfirm:
      'Irreversible. El tráfico se detiene al instante, las conversaciones se archivan, se retira el acceso a los repositorios compartidos y se cancela cualquier ejecución que dependa de él.',
    status: {
      draft: 'Borrador',
      proposed: 'Propuesto',
      accepted: 'Activo',
      rejected: 'Rechazado',
      suspended: 'Suspendido',
      expired: 'Caducado',
      revoked: 'Revocado',
    },
    panel: {
      topics: 'Temas',
      delegation: 'Delegación de tareas',
      sharedRepos: 'Repositorios compartidos',
      fileTransfer: 'Transferencia de ficheros',
      allowed: 'permitida',
      notAllowed: 'no permitida',
      messagesPerDay: 'Mensajes al día',
      heldForApproval: 'Retenido para aprobación',
      teamsOurSide: 'Equipos: nuestro lado',
      teamsTheirSide: 'Equipos: su lado',
      rejected: 'Rechazado: {reason}',
      lastSevenDays: 'Últimos 7 días: {count} mensajes',
      delegations: 'Delegaciones: {count}',
      blockedAttempts: 'Intentos bloqueados: {count}',
      viewConversations: 'Ver las conversaciones',
      decide: 'Decidir',
      decideHint:
        'Aceptar permite que los miembros de los equipos designados intercambien mensajes sobre {topics}, y nada más. Todo lo que quede fuera del alcance se deniega y se audita. Elige cuáles de tus equipos de comunicación sostienen la puerta:',
      noTopics: 'ningún tema',
      noCommunicationTeam:
        'Todavía no tienes ningún equipo de comunicación. Puedes aceptar ahora y designar uno después, pero no circulará nada hasta que lo hagas.',
      members: { one: '{count} miembro', other: '{count} miembros' },
      accept: 'Aceptar',
      rejectionReason: 'Motivo del rechazo',
      rejectionPlaceholder: 'Motivo (si rechazas)',
      reject: 'Rechazar',
    },
  },

  proposal: {
    title: 'Proponer un acuerdo',
    draftFailed: 'No se ha podido crear el borrador.',
    theCounterpart: 'la contraparte',
    step1: '1 · Contraparte',
    counterpartLabel: 'Empresa contraparte',
    chooseCompany: 'Elige una empresa de este despliegue…',
    step2: '2 · Tus equipos de comunicación',
    noTeams:
      'Todavía no hay ningún equipo de comunicación. Crea uno de tipo «comunicación» en Organización primero: es la única puerta que abre el acuerdo.',
    members: { one: '{count} miembro', other: '{count} miembros' },
    step3: '3 · Alcance',
    agreementTitle: 'Título',
    topics: 'Temas permitidos (separados por comas)',
    allowDelegation: 'Permitir delegar tareas a través de la frontera',
    allowRepoSharing: 'Permitir compartir repositorios',
    sharedRepos: 'Repositorios compartidos (empresa/repo:acceso, separados por comas)',
    messagesPerDay: 'Mensajes al día, en ambos sentidos',
    step4: '4 · Términos',
    terms: 'Términos',
    termsPlaceholder: 'Términos en formato libre, en Markdown.',
    confidentiality: 'Nota de confidencialidad: se inyecta literalmente en los prompts de ambos equipos',
    step5: '5 · Vigencia',
    validUntil: 'Válido hasta (vacío = indefinido)',
    step6: '6 · Qué se está proponiendo',
    summary:
      'Tus equipos podrán intercambiar mensajes con {counterpart} sobre {topics}. Delegación de tareas {delegation} · repositorios {repos} · todo lo demás se deniega y se audita.',
    noTopics: 'ningún tema: no circulará nada',
    allowed: 'permitida',
    notAllowed: 'no permitida',
    shared: 'compartidos según la lista',
    notShared: 'no compartidos',
    createDraft: 'Crear el borrador',
  },

  conversations: {
    title: 'Conversaciones',
    intro:
      'Qué se están diciendo los empleados, incluido lo que no se les dejó decir. Los intentos bloqueados aparecen en el hilo al que apuntaban, con la política que los detuvo.',
    live: 'en vivo',
    offline: 'sin conexión',
    status: 'Estado',
    allThreads: 'Todos los hilos',
    open: 'Abiertos',
    resolved: 'Resueltos',
    archived: 'Archivados',
    empty: 'Aún no hay conversaciones',
    emptyHint: 'Los empleados abren hilos con las herramientas de absorbus cuando se escriben entre ellos.',
    backToThreads: 'Volver a los hilos',
    pickThread: 'Elige un hilo',
    pickThreadHint: 'Aquí aparecerán sus mensajes y sus intentos bloqueados.',
    task: 'tarea #{number}',
    archivedState: 'Archivado',
    archive: 'Archivar',
    messages: '{count} mensajes',
    archivedSuffix: ' · archivado',
    interCompany: ' · entre empresas',
    escalation: 'escalado',
    delegation: 'delegación',
    urgent: 'urgente',
    redacted: 'censurado',
    redactedTitle: 'Se ha censurado un secreto antes de guardarlo.',
    recipients: 'destinatarios',
    blocked: 'BLOQUEADO',
  },

  webhooks: {
    title: 'Webhooks salientes',
    intro:
      'POST firmados a tus sistemas cuando aquí pasa algo. Seis reintentos a lo largo de seis horas; veinte fallos seguidos desactivan el webhook y te avisan.',
    newWebhook: 'Nuevo webhook',
    createTitle: 'Webhook nuevo',
    empty: 'Aún no hay webhooks',
    emptyHint:
      'Empuja los eventos de tareas, presupuestos y aprobaciones a Slack, a tu sistema de tickets o a cualquier cosa con un endpoint HTTPS.',
    createOne: 'Crear un webhook',
    name: 'Nombre',
    url: 'URL',
    urlHint: 'Solo HTTPS, alcanzable desde internet: las direcciones privadas se rechazan.',
    httpsRequired: 'La URL tiene que empezar por https://; el http plano no se acepta.',
    pickEvent: 'Elige al menos un evento, o suscríbete a todos.',
    create: 'Crear el webhook',
    events: 'Eventos',
    allEvents: 'Todos los eventos ({star}), incluidos los tipos que se añadan en el futuro',
    allEventsShort: 'todos los eventos',
    filterEvents: 'Filtrar eventos',
    filterPlaceholder: 'Filtrar eventos…',
    secretFor: 'Secreto de {name}',
    secretPanel: 'Secreto del webhook',
    copyNow: 'Cópialo ahora: {emphasis}. Tu endpoint lo usa para verificar la cabecera {header} de cada entrega.',
    notShownAgain: 'no se volverá a enseñar',
    done: 'Listo',
    consecutiveFailures: '{count} fallos seguidos',
    lastDelivery: '· última entrega {when}',
    never: 'nunca',
    sendTest: 'Enviar una prueba',
    rotateSecret: 'Rotar el secreto',
    enable: 'Activar',
    disable: 'Desactivar',
    testQueued: 'Prueba encolada: el resultado aparece en la lista de entregas de abajo.',
    testResult: 'Prueba {status}{code}',
    httpCode: ' — HTTP {code}',
    rotateWarning:
      'Rotar invalida el secreto actual {emphasis}; las entregas firmadas con él fallarán hasta que tu endpoint use el nuevo.',
    immediately: 'de inmediato',
    rotate: 'Rotar',
    deliveries: 'Entregas',
    noDeliveries: 'Todavía no hay entregas.',
    retryNow: 'Reintentar ahora',
    deleteConfirm:
      'Las entregas se detienen de inmediato, el histórico de entregas se descarta y el secreto se destruye. Los sistemas que escuchen en este endpoint simplemente dejarán de saber de nosotros.',
    table: {
      event: 'Evento',
      status: 'Estado',
      attempts: 'Intentos',
      code: 'Código',
      nextRetry: 'Próximo reintento',
      when: 'Cuándo',
    },
    status: {
      active: 'activo',
      failing: 'fallando',
      disabled: 'desactivado',
    },
    deliveryStatus: {
      pending: 'pendiente',
      delivered: 'entregada',
      failed: 'fallida',
      abandoned: 'abandonada',
    },
  },

  language: {
    label: 'Idioma',
    interface: 'Idioma de la interfaz',
    change: 'Cambiar de idioma',
  },
} as const;
